import { stringifyLosslessJson } from './lossless-json-codec';
import {
    IpgSystemId,
    IpgSystemIdKey,
    IpgSystemIdKeyList,
    IpwSystemId,
    IpwSystemIdKey,
    IpwSystemIdKeyList,
    ThirdPartySystemId,
} from './types/third-party-system.enums';
import { IpgRpaModuleName } from './types/ipg/ipg-rpa-module-name-registry';
import {
    NumericIpgSystemIdSchema,
    ThirdPartySystemIdSchema,
} from './schemas/third-party-system.schemas';

import { z } from 'zod';
import {
    buildStoredJsonParser,
    buildStoredJsonValueParser,
    defineStoredJsonContract,
    defineStoredJsonSemanticFieldValue,
} from './stored-json-contract.types';
import {
    StoredLosslessNumberSchema,
    convertStoredJsonNumberToBoundedNumber,
    normalizeStoredJsonSafeIntegerObjectFields,
} from './stored-json-number';
import {
    createIpgConfigSchemaRegistry,
    type IpgConfig,
    type IpgConfigCustomization,
    type IpgConfigCustomizationMap,
    type IpgConfigStorageMap,
    type UpdateIpgConfigCustomizationMap,
    normalizeLegacyIpgConfigMap,
    validateLegacyIpgConfigMap,
} from './types/ipg/ipg-backend-config-registry';

export type {
    IpgConfig,
    IpgConfigCustomization,
    IpgConfigCustomizationMap,
    UpdateIpgConfigCustomizationMap,
} from './types/ipg/ipg-backend-config-registry';

export const RpaModuleName = {
    PCPARTPICKER_IPW: 'pcpartpicker-ipw' as const,
    DEALERTRACK_IPW: 'dealertrack-ipw' as const,
    ...IpgRpaModuleName,
    PCPARTPICKER_WORKFLOWS: 'pcpartpicker-workflows' as const,
    STONEPROFITS_WORKFLOWS: 'stoneprofits-workflows' as const,
    GENERIC_WEBSITE_WORKFLOWS: 'generic-website-workflows' as const,
} as const;
export type RpaModuleName = (typeof RpaModuleName)[keyof typeof RpaModuleName];
export const ALL_RPA_MODULE_NAMES: RpaModuleName[] = Object.values(RpaModuleName);
const RpaModuleNameValueSet: ReadonlySet<string> = new Set(ALL_RPA_MODULE_NAMES);
export const RpaModuleNameSchema: z.ZodType<RpaModuleName> = z.enum(
    ALL_RPA_MODULE_NAMES
);

export function isRpaModuleName(moduleName: string): moduleName is RpaModuleName {
    return RpaModuleNameValueSet.has(moduleName);
}

export const SessionPersistenceType = {
    EPHEMERAL: 'ephemeral' as const,
    PERSISTENT: 'persistent' as const,
} as const;
export type SessionPersistenceType =
    (typeof SessionPersistenceType)[keyof typeof SessionPersistenceType];

export const IpWebsiteType = {
    PORTAL: 'portal' as const,
    IPG: 'ipg' as const,
    IPW: 'ipw' as const,
    UNMANAGED: 'unmanaged' as const,
} as const;
export type IpWebsiteType = (typeof IpWebsiteType)[keyof typeof IpWebsiteType];

export const MerchantWebsiteType = {
    IPG: IpWebsiteType.IPG,
    IPW: IpWebsiteType.IPW,
} as const;
export type MerchantWebsiteType = (typeof MerchantWebsiteType)[keyof typeof MerchantWebsiteType];
// Zod schema for SessionPersistenceType
export const SessionPersistenceTypeSchema = z.union([
    z.literal('ephemeral'),
    z.literal('persistent'),
]);

export const UrlPatternsRuleAction = {
    ALLOW: 'allow' as const,
    DENY: 'deny' as const,
} as const;
export type UrlPatternsRuleAction =
    (typeof UrlPatternsRuleAction)[keyof typeof UrlPatternsRuleAction];

export const UrlPatternsRuleActionSchema: z.ZodType<UrlPatternsRuleAction> = z.enum([
    UrlPatternsRuleAction.ALLOW,
    UrlPatternsRuleAction.DENY,
]);

export function isValidUrlPatternString(pattern: string): boolean {
    try {
        new URLPattern(pattern);
        return true;
    } catch {
        return false;
    }
}

export type UrlPatternString = string;

export const UrlPatternStringSchema: z.ZodType<UrlPatternString> = z
    .string()
    .min(1)
    .refine(isValidUrlPatternString, {
        message: 'Invalid URLPattern syntax.',
    });

export const UrlPatternStringArraySchema: z.ZodType<UrlPatternString[]> = z
    .array(UrlPatternStringSchema)
    .min(1);

export const UrlPatternStringListSchema: z.ZodType<UrlPatternString[]> =
    UrlPatternStringArraySchema;

export const UrlPatternStringListStorageVersion = {
    V1: 1,
    V2: 2,
} as const;
export type UrlPatternStringListStorageVersion =
    (typeof UrlPatternStringListStorageVersion)[keyof typeof UrlPatternStringListStorageVersion];

export type UrlPatternStringListV1 = UrlPatternString[];

export interface UrlPatternStringListV2 {
    readonly url_pattern_list_version:
        typeof UrlPatternStringListStorageVersion.V2;
    readonly patterns: UrlPatternString[];
}

export type StoredUrlPatternStringList =
    | UrlPatternStringListV1
    | UrlPatternStringListV2;

export const UrlPatternStringListStorageVersionSchema: z.ZodType<UrlPatternStringListStorageVersion> =
    z.union([z.literal(1), z.literal(2)]);

export const UrlPatternStringListV1Schema: z.ZodType<UrlPatternStringListV1> =
    UrlPatternStringArraySchema;

export const UrlPatternStringListV2Schema: z.ZodType<UrlPatternStringListV2> =
    z
        .object({
            url_pattern_list_version: z.literal(2),
            patterns: UrlPatternStringArraySchema,
        })
        .strict();

export const StoredUrlPatternStringListV2Schema:
    z.ZodType<UrlPatternStringListV2> = z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                domain: 'url-pattern-list',
                fields: ['url_pattern_list_version'],
                minimum: 0,
                maximum: Number.MAX_SAFE_INTEGER,
            }),
        UrlPatternStringListV2Schema
    );

export const StoredUrlPatternStringListSchema: z.ZodType<StoredUrlPatternStringList> =
    z.union([
        StoredUrlPatternStringListV2Schema,
        UrlPatternStringListV1Schema,
    ]);

export function normalizeUrlPatternStringListV1(
    patternList: UrlPatternStringListV1
): UrlPatternString[] {
    return patternList;
}

export function normalizeUrlPatternStringListV2(
    patternList: UrlPatternStringListV2
): UrlPatternString[] {
    return patternList.patterns;
}

export const UrlPatternStringListStorageContract = defineStoredJsonContract({
    contractName: 'UrlPatternString[]',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'UrlPatternStringListV1Schema',
            schema: UrlPatternStringListV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['url_pattern_list_version'],
                },
            ],
            normalize: normalizeUrlPatternStringListV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredUrlPatternStringListV2Schema',
            schema: StoredUrlPatternStringListV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['url_pattern_list_version'],
                    markerValue: UrlPatternStringListStorageVersion.V2,
                },
            ],
            normalize: normalizeUrlPatternStringListV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredUrlPatternStringListRowParser: z.ZodType<UrlPatternString[]> =
    buildStoredJsonParser(UrlPatternStringListStorageContract);

export const StoredUrlPatternStringListParser: z.ZodType<UrlPatternString[]> =
    z.preprocess(
        (patternList: unknown): unknown => ({ value: patternList }),
        StoredUrlPatternStringListRowParser
    );

export function serializeUrlPatternStringListV2(
    patterns: readonly UrlPatternString[]
): UrlPatternStringListV2 {
    return {
        url_pattern_list_version: 2,
        patterns: UrlPatternStringArraySchema.parse(patterns),
    };
}

export const UniqueUrlPatternStringListSchema: z.ZodType<UrlPatternString[]> =
    z.array(UrlPatternStringSchema).superRefine((patterns, ctx) => {
        const seenPatterns: Set<string> = new Set();
        for (const [index, pattern] of patterns.entries()) {
            if (seenPatterns.has(pattern)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Duplicate URLPattern string.',
                    path: [index],
                });
            }
            seenPatterns.add(pattern);
        }
    });

export const NonEmptyUniqueUrlPatternStringListSchema: z.ZodType<UrlPatternString[]> =
    UniqueUrlPatternStringListSchema.refine(
        (patterns) => patterns.length > 0,
        {
            message: 'At least one URLPattern string is required.',
        }
    );

export function canonicalizeUrlPatternStrings(
    patterns: readonly string[]
): UrlPatternString[] {
    if (patterns.length === 0) {
        throw new Error('At least one URLPattern string is required.');
    }

    const validPatterns: UrlPatternString[] = [];
    for (const pattern of patterns) {
        if (pattern.trim() !== pattern) {
            throw new Error('URLPattern strings may not include surrounding whitespace.');
        }
        validPatterns.push(UrlPatternStringSchema.parse(pattern));
    }

    return [...new Set(validPatterns)].sort();
}

export type UrlPatternsRule = {
    action: UrlPatternsRuleAction;
    urlPatterns: UrlPatternString[];
};

export const UrlPatternsRuleSchema: z.ZodType<UrlPatternsRule> = z.object({
    action: UrlPatternsRuleActionSchema,
    urlPatterns: UrlPatternStringArraySchema,
});
export type UrlPatternsRules = UrlPatternsRule;
export const UrlPatternsRulesSchema = UrlPatternsRuleSchema;

export interface AllowedWebsiteBase {
    system_id: ThirdPartySystemId;
    template_name?: string;
    name: string;
    url: string;
    allowedUrlPatterns: UrlPatternString[];
    sessionPersistence: SessionPersistenceType;
    promoteSessionCookies: boolean;
    urlPatternsRules: UrlPatternsRule[];
    fetchDefaultAction: UrlPatternsRuleAction;
    iframeUrlPatterns: UrlPatternsRule[];
    iframeDefaultAction: UrlPatternsRuleAction;
}

export type MerchantAllowedWebsite = AllowedWebsiteBase & { type: MerchantWebsiteType };
export type IpwAllowedWebsite = AllowedWebsiteBase & { type: typeof IpWebsiteType.IPW };
export type IpgAllowedWebsite = AllowedWebsiteBase & { type: typeof IpWebsiteType.IPG };
export type PortalAllowedWebsite = AllowedWebsiteBase & { type: typeof IpWebsiteType.PORTAL };
export type UnmanagedAllowedWebsite = AllowedWebsiteBase & {
    type: typeof IpWebsiteType.UNMANAGED;
};

// AllowedWebsite: discriminated union of PORTAL, IPW, IPG, UNMANAGED.
// TODO: Add a discriminated union zod schema.
export type AllowedWebsite =
    | UnmanagedAllowedWebsite
    | MerchantAllowedWebsite
    | PortalAllowedWebsite;

// Zod schema for AllowedWebsite
export const AllowedWebsiteSchemaBase: z.ZodObject<{
    system_id: typeof ThirdPartySystemIdSchema;
    template_name: z.ZodOptional<z.ZodString>;
    name: z.ZodString;
    url: z.ZodString;
    allowedUrlPatterns: typeof UrlPatternStringArraySchema;
    sessionPersistence: typeof SessionPersistenceTypeSchema;
    promoteSessionCookies: z.ZodDefault<z.ZodBoolean>;
    urlPatternsRules: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    fetchDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    iframeUrlPatterns: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    iframeDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
}> = z.object({
    system_id: ThirdPartySystemIdSchema,
    template_name: z.string().trim().min(1).optional(),
    name: z.string(),
    url: z.string().url(),
    allowedUrlPatterns: UrlPatternStringArraySchema,
    sessionPersistence: SessionPersistenceTypeSchema,
    promoteSessionCookies: z.boolean().default(false),
    urlPatternsRules: z.array(UrlPatternsRuleSchema).default([]),
    fetchDefaultAction: UrlPatternsRuleActionSchema.default(UrlPatternsRuleAction.ALLOW),
    iframeUrlPatterns: z.array(UrlPatternsRuleSchema).default([]),
    iframeDefaultAction: UrlPatternsRuleActionSchema.default(UrlPatternsRuleAction.ALLOW),
});

export const AllowedWebsiteSchemaIPG: z.ZodObject<{
    system_id: typeof ThirdPartySystemIdSchema;
    template_name: z.ZodOptional<z.ZodString>;
    name: z.ZodString;
    url: z.ZodString;
    allowedUrlPatterns: typeof UrlPatternStringArraySchema;
    sessionPersistence: typeof SessionPersistenceTypeSchema;
    promoteSessionCookies: z.ZodDefault<z.ZodBoolean>;
    urlPatternsRules: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    fetchDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    iframeUrlPatterns: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    iframeDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    type: z.ZodDefault<z.ZodLiteral<'ipg'>>;
}> = AllowedWebsiteSchemaBase.extend({
    type: z.literal(MerchantWebsiteType.IPG).default(MerchantWebsiteType.IPG),
});

export const AllowedWebsiteSchemaIPW: z.ZodObject<{
    system_id: typeof ThirdPartySystemIdSchema;
    template_name: z.ZodOptional<z.ZodString>;
    name: z.ZodString;
    url: z.ZodString;
    allowedUrlPatterns: typeof UrlPatternStringArraySchema;
    sessionPersistence: typeof SessionPersistenceTypeSchema;
    promoteSessionCookies: z.ZodDefault<z.ZodBoolean>;
    urlPatternsRules: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    fetchDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    iframeUrlPatterns: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    iframeDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    type: z.ZodDefault<z.ZodLiteral<'ipw'>>;
}> = AllowedWebsiteSchemaBase.extend({
    type: z.literal(MerchantWebsiteType.IPW).default(MerchantWebsiteType.IPW),
});

export const AllowedWebsiteSchemaUnmanaged: z.ZodObject<{
    system_id: typeof ThirdPartySystemIdSchema;
    template_name: z.ZodOptional<z.ZodString>;
    name: z.ZodString;
    url: z.ZodString;
    allowedUrlPatterns: typeof UrlPatternStringArraySchema;
    sessionPersistence: typeof SessionPersistenceTypeSchema;
    promoteSessionCookies: z.ZodDefault<z.ZodBoolean>;
    urlPatternsRules: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    fetchDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    iframeUrlPatterns: z.ZodDefault<z.ZodArray<typeof UrlPatternsRuleSchema>>;
    iframeDefaultAction: z.ZodDefault<typeof UrlPatternsRuleActionSchema>;
    type: z.ZodLiteral<'unmanaged'>;
}> = AllowedWebsiteSchemaBase.extend({
    type: z.literal(IpWebsiteType.UNMANAGED),
});

export const UnmanagedAllowedWebsiteStorageVersion = {
    V2: 2,
} as const;

export type UnmanagedAllowedWebsiteStorageVersion =
    (typeof UnmanagedAllowedWebsiteStorageVersion)[keyof typeof UnmanagedAllowedWebsiteStorageVersion];

export type UnmanagedAllowedWebsiteStorageV1 = UnmanagedAllowedWebsite;

export interface UnmanagedAllowedWebsiteStorageV2 {
    readonly website_config_version:
        typeof UnmanagedAllowedWebsiteStorageVersion.V2;
    readonly config: UnmanagedAllowedWebsite;
}

export type StoredUnmanagedAllowedWebsite =
    | UnmanagedAllowedWebsiteStorageV1
    | UnmanagedAllowedWebsiteStorageV2;

export const UnmanagedAllowedWebsiteStorageVersionSchema:
    z.ZodType<UnmanagedAllowedWebsiteStorageVersion> =
    z.literal(UnmanagedAllowedWebsiteStorageVersion.V2);

export const UnmanagedAllowedWebsiteStorageV1Schema:
    z.ZodType<UnmanagedAllowedWebsiteStorageV1> =
    AllowedWebsiteSchemaUnmanaged;

export const UnmanagedAllowedWebsiteStorageV2Schema:
    z.ZodType<UnmanagedAllowedWebsiteStorageV2> =
    z.object({
        website_config_version:
            UnmanagedAllowedWebsiteStorageVersionSchema,
        config: AllowedWebsiteSchemaUnmanaged,
    }).strict();

function normalizeStoredUnmanagedAllowedWebsiteV2(value: unknown): unknown {
    const normalizedRoot = normalizeStoredJsonSafeIntegerObjectFields(
        value,
        {
            domain: 'unmanaged-allowed-website',
            fields: ['website_config_version'],
            minimum: 0,
            maximum: Number.MAX_SAFE_INTEGER,
        }
    );
    if (!isPlainRecord(normalizedRoot)) {
        return normalizedRoot;
    }
    return {
        ...normalizedRoot,
        config: normalizeStoredJsonSafeIntegerObjectFields(
            normalizedRoot.config,
            {
                domain: 'unmanaged-allowed-website.config',
                fields: ['system_id'],
                minimum: 0,
                maximum: Number.MAX_SAFE_INTEGER,
            }
        ),
    };
}

export const StoredUnmanagedAllowedWebsiteStorageV1Schema:
    z.ZodType<UnmanagedAllowedWebsiteStorageV1> = z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                domain: 'unmanaged-allowed-website',
                fields: ['system_id'],
                minimum: 0,
                maximum: Number.MAX_SAFE_INTEGER,
            }),
        UnmanagedAllowedWebsiteStorageV1Schema
    );

export const StoredUnmanagedAllowedWebsiteStorageV2Schema:
    z.ZodType<UnmanagedAllowedWebsiteStorageV2> = z.preprocess(
        normalizeStoredUnmanagedAllowedWebsiteV2,
        UnmanagedAllowedWebsiteStorageV2Schema
    );

export const UnmanagedAllowedWebsiteVersionedObjectMarkerSchema:
    z.ZodType<{ readonly website_config_version: unknown }> =
    z.object({
        website_config_version: z.unknown(),
    }).passthrough();

export const StoredUnmanagedAllowedWebsiteSchema:
    z.ZodType<StoredUnmanagedAllowedWebsite> =
    z.union([
        StoredUnmanagedAllowedWebsiteStorageV2Schema,
        StoredUnmanagedAllowedWebsiteStorageV1Schema,
    ]);

export function normalizeUnmanagedAllowedWebsiteStorageV1(
    config: UnmanagedAllowedWebsiteStorageV1
): UnmanagedAllowedWebsite {
    return config;
}

export function normalizeUnmanagedAllowedWebsiteStorageV2(
    config: UnmanagedAllowedWebsiteStorageV2
): UnmanagedAllowedWebsite {
    return config.config;
}

export const UnmanagedAllowedWebsiteStorageContract = defineStoredJsonContract({
    contractName: 'UnmanagedAllowedWebsite',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredUnmanagedAllowedWebsiteStorageV1Schema',
            schema: StoredUnmanagedAllowedWebsiteStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['website_config_version'],
                },
            ],
            normalize: normalizeUnmanagedAllowedWebsiteStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredUnmanagedAllowedWebsiteStorageV2Schema',
            schema: StoredUnmanagedAllowedWebsiteStorageV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['website_config_version'],
                    markerValue: UnmanagedAllowedWebsiteStorageVersion.V2,
                },
            ],
            normalize: normalizeUnmanagedAllowedWebsiteStorageV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredUnmanagedAllowedWebsiteParser:
    z.ZodType<UnmanagedAllowedWebsite> =
    buildStoredJsonValueParser(UnmanagedAllowedWebsiteStorageContract);

export function serializeUnmanagedAllowedWebsiteV2(
    config: UnmanagedAllowedWebsite
): UnmanagedAllowedWebsiteStorageV2 {
    return {
        website_config_version: UnmanagedAllowedWebsiteStorageVersion.V2,
        config: AllowedWebsiteSchemaUnmanaged.parse(config),
    };
}

// Zod schema for IpwConfig
export interface IpwReconciliationConfig {
    paymentCode: string;
    doInjectApprovalNumber: boolean;
    doFinalizeReconciliation: boolean;
}

export interface IpwConfigBase {
    ipw_id: number;
    ipw_system_id: IpwSystemIdKey;
    name: string;
    urlPattern: UrlPatternString;
    workflow_prefix: string;
    rpaModules: RpaModuleName[];
    allowedWebsites: IpwAllowedWebsite[];
    reconciliation: IpwReconciliationConfig;
}

export interface IpwConfigDealertrack extends IpwConfigBase {
    ipw_system_id: typeof IpwSystemIdKey.DEALERTRACK;
}

export interface IpwConfigPcpartpicker extends IpwConfigBase {
    ipw_system_id: typeof IpwSystemIdKey.PCPARTPICKER;
}

export type IpwConfig =
    | IpwConfigPcpartpicker
    | IpwConfigDealertrack;

export const IpwReconciliationConfigSchema: z.ZodType<IpwReconciliationConfig> =
    z
        .object({
            paymentCode: z.string().max(2),
            doInjectApprovalNumber: z.boolean(),
            doFinalizeReconciliation: z.boolean(),
        })
        .strict();

export const IpwConfigBaseSchema: z.ZodObject<{
    ipw_id: z.ZodNumber;
    ipw_system_id: z.ZodType<IpwSystemIdKey>;
    name: z.ZodString;
    urlPattern: typeof UrlPatternStringSchema;
    workflow_prefix: z.ZodString;
    allowedWebsites: z.ZodDefault<z.ZodArray<typeof AllowedWebsiteSchemaIPW>>;
    rpaModules: z.ZodDefault<z.ZodArray<typeof RpaModuleNameSchema>>;
    reconciliation: typeof IpwReconciliationConfigSchema;
}> = z.object({
    ipw_id: z.number(),
    ipw_system_id: z.enum(IpwSystemIdKeyList),
    name: z.string(),
    urlPattern: UrlPatternStringSchema,
    workflow_prefix: z.string(),
    allowedWebsites: z.array(AllowedWebsiteSchemaIPW).default([]),
    rpaModules: z.array(RpaModuleNameSchema).default([]),
    reconciliation: IpwReconciliationConfigSchema,
});

export const IpwConfigDealertrackSchema: z.ZodObject<{
    ipw_id: z.ZodLiteral<typeof IpwSystemId.DEALERTRACK>;
    ipw_system_id: z.ZodLiteral<typeof IpwSystemIdKey.DEALERTRACK>;
    name: z.ZodString;
    urlPattern: typeof UrlPatternStringSchema;
    workflow_prefix: z.ZodString;
    allowedWebsites: z.ZodDefault<z.ZodArray<typeof AllowedWebsiteSchemaIPW>>;
    rpaModules: z.ZodDefault<z.ZodArray<typeof RpaModuleNameSchema>>;
    reconciliation: typeof IpwReconciliationConfigSchema;
}> = IpwConfigBaseSchema.extend({
    ipw_id: z.literal(IpwSystemId.DEALERTRACK),
    ipw_system_id: z.literal(IpwSystemIdKey.DEALERTRACK),
});

export const IpwConfigPcpartpickerSchema: z.ZodObject<{
    ipw_id: z.ZodLiteral<typeof IpwSystemId.PCPARTPICKER>;
    ipw_system_id: z.ZodLiteral<typeof IpwSystemIdKey.PCPARTPICKER>;
    name: z.ZodString;
    urlPattern: typeof UrlPatternStringSchema;
    workflow_prefix: z.ZodString;
    allowedWebsites: z.ZodDefault<z.ZodArray<typeof AllowedWebsiteSchemaIPW>>;
    rpaModules: z.ZodDefault<z.ZodArray<typeof RpaModuleNameSchema>>;
    reconciliation: typeof IpwReconciliationConfigSchema;
}> = IpwConfigBaseSchema.extend({
    ipw_id: z.literal(IpwSystemId.PCPARTPICKER),
    ipw_system_id: z.literal(IpwSystemIdKey.PCPARTPICKER),
});

export const IpwConfigSchema: z.ZodDiscriminatedUnion<
    [
        typeof IpwConfigPcpartpickerSchema,
        typeof IpwConfigDealertrackSchema,
    ],
    'ipw_system_id'
> = z.discriminatedUnion(
    'ipw_system_id',
    [
        IpwConfigPcpartpickerSchema,
        IpwConfigDealertrackSchema,
    ]
);

export interface OrganizationRpaBuilderWebsiteWorkflowInputDefaults {
    payment_type_code?: string;
}

export const RbsPaymentTypeCodeSchema: z.ZodType<string> = z
    .string()
    .refine((value) => value.trim().length > 0, {
        message: 'Payment type code must not be empty.',
    });

export const OrganizationRpaBuilderWebsiteWorkflowInputDefaultsSchema: z.ZodType<OrganizationRpaBuilderWebsiteWorkflowInputDefaults> =
    z.object({
        payment_type_code: RbsPaymentTypeCodeSchema.optional(),
    }).strict();

export interface RpaBuilderWebsiteConfig {
    ips_trigger_url_patterns: UrlPatternString[];
    workflow_input_defaults?: OrganizationRpaBuilderWebsiteWorkflowInputDefaults;
}

export const RpaBuilderWebsiteConfigSchema: z.ZodType<RpaBuilderWebsiteConfig> =
    z.object({
        ips_trigger_url_patterns: UniqueUrlPatternStringListSchema,
        workflow_input_defaults:
            OrganizationRpaBuilderWebsiteWorkflowInputDefaultsSchema.optional(),
    }).strict();

export const OrganizationRpaBuilderWebsiteRequiredTriggerConfigSchema: z.ZodType<RpaBuilderWebsiteConfig> =
    z.object({
        ips_trigger_url_patterns: NonEmptyUniqueUrlPatternStringListSchema,
        workflow_input_defaults:
            OrganizationRpaBuilderWebsiteWorkflowInputDefaultsSchema.optional(),
    }).strict();

export const RpaBuilderWebsiteConfigStorageVersion = {
    V2: 2,
} as const;

export type RpaBuilderWebsiteConfigStorageVersion =
    (typeof RpaBuilderWebsiteConfigStorageVersion)[keyof typeof RpaBuilderWebsiteConfigStorageVersion];

export type RpaBuilderWebsiteConfigStorageV1 = RpaBuilderWebsiteConfig;

export interface RpaBuilderWebsiteConfigStorageV2 {
    readonly rbs_config_version:
        typeof RpaBuilderWebsiteConfigStorageVersion.V2;
    readonly config: RpaBuilderWebsiteConfig;
}

export type StoredRpaBuilderWebsiteConfig =
    | RpaBuilderWebsiteConfigStorageV1
    | RpaBuilderWebsiteConfigStorageV2;

export const RpaBuilderWebsiteConfigStorageVersionSchema:
    z.ZodType<RpaBuilderWebsiteConfigStorageVersion> =
    z.literal(RpaBuilderWebsiteConfigStorageVersion.V2);

export const RpaBuilderWebsiteConfigStorageV1Schema:
    z.ZodType<RpaBuilderWebsiteConfigStorageV1> =
    RpaBuilderWebsiteConfigSchema;

export const RpaBuilderWebsiteConfigStorageV2Schema:
    z.ZodType<RpaBuilderWebsiteConfigStorageV2> =
    z.object({
        rbs_config_version:
            RpaBuilderWebsiteConfigStorageVersionSchema,
        config: RpaBuilderWebsiteConfigSchema,
    }).strict();

export const StoredRpaBuilderWebsiteConfigStorageV2Schema:
    z.ZodType<RpaBuilderWebsiteConfigStorageV2> = z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                domain: 'rpa-builder-website-config',
                fields: ['rbs_config_version'],
                minimum: 0,
                maximum: Number.MAX_SAFE_INTEGER,
            }),
        RpaBuilderWebsiteConfigStorageV2Schema
    );

export const RpaBuilderWebsiteConfigVersionedObjectMarkerSchema:
    z.ZodType<{ readonly rbs_config_version: unknown }> =
    z.object({
        rbs_config_version: z.unknown(),
    }).passthrough();

export const StoredRpaBuilderWebsiteConfigSchema:
    z.ZodType<StoredRpaBuilderWebsiteConfig> =
    z.union([
        StoredRpaBuilderWebsiteConfigStorageV2Schema,
        RpaBuilderWebsiteConfigStorageV1Schema,
    ]);

export function normalizeRpaBuilderWebsiteConfigStorageV1(
    config: RpaBuilderWebsiteConfigStorageV1
): RpaBuilderWebsiteConfig {
    return config;
}

export function normalizeRpaBuilderWebsiteConfigStorageV2(
    config: RpaBuilderWebsiteConfigStorageV2
): RpaBuilderWebsiteConfig {
    return config.config;
}

export const RpaBuilderWebsiteConfigStorageContract = defineStoredJsonContract({
    contractName: 'RpaBuilderWebsiteConfig',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'RpaBuilderWebsiteConfigStorageV1Schema',
            schema: RpaBuilderWebsiteConfigStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['rbs_config_version'],
                },
            ],
            normalize: normalizeRpaBuilderWebsiteConfigStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredRpaBuilderWebsiteConfigStorageV2Schema',
            schema: StoredRpaBuilderWebsiteConfigStorageV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['rbs_config_version'],
                    markerValue: RpaBuilderWebsiteConfigStorageVersion.V2,
                },
            ],
            normalize: normalizeRpaBuilderWebsiteConfigStorageV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredRpaBuilderWebsiteConfigParser:
    z.ZodType<RpaBuilderWebsiteConfig> =
    buildStoredJsonValueParser(RpaBuilderWebsiteConfigStorageContract);

export function serializeRpaBuilderWebsiteConfigV2(
    config: RpaBuilderWebsiteConfig
): RpaBuilderWebsiteConfigStorageV2 {
    return {
        rbs_config_version: RpaBuilderWebsiteConfigStorageVersion.V2,
        config: RpaBuilderWebsiteConfigSchema.parse(config),
    };
}

export const RbsMerchantWebsiteUuidSchema: z.ZodType<string> =
    z.string().uuid();

export interface RbsMerchantWebsiteConfig {
    organization_uuid: string;
    organization_allowed_website_uuid: string;
    allowed_website_template_uuid?: string;
    website_config: UnmanagedAllowedWebsite;
    rbs_config: RpaBuilderWebsiteConfig;
}

export const RbsMerchantWebsiteConfigSchema: z.ZodType<RbsMerchantWebsiteConfig> =
    z.object({
        organization_uuid: RbsMerchantWebsiteUuidSchema,
        organization_allowed_website_uuid: RbsMerchantWebsiteUuidSchema,
        allowed_website_template_uuid:
            RbsMerchantWebsiteUuidSchema.optional(),
        website_config: AllowedWebsiteSchemaUnmanaged,
        rbs_config: RpaBuilderWebsiteConfigSchema,
    }).strict();

export interface RbsConfig {
    websites: RbsMerchantWebsiteConfig[];
}

export const RbsConfigSchema: z.ZodType<RbsConfig> = z.object({
    websites: z.array(RbsMerchantWebsiteConfigSchema),
}).strict();


// Zod schema for IpgConfig
export interface BaseIpgConfig {
    ipg_id: IpgSystemId;
    name: string;
    urlPattern: UrlPatternString;
    workflow_prefix: string;
    allowedWebsites: IpgAllowedWebsite[];
    rpaModules: RpaModuleName[];
}

export interface IpgConfigWithGatewayDefaults extends BaseIpgConfig {
    default_api_url?: string;
    default_channel?: string;
}

export type LegacyIpgConfig = IpgConfigWithGatewayDefaults;

export const BaseIpgConfigSchema: z.ZodObject<{
    ipg_id: typeof NumericIpgSystemIdSchema;
    name: z.ZodString;
    urlPattern: typeof UrlPatternStringSchema;
    workflow_prefix: z.ZodString;
    allowedWebsites: z.ZodDefault<z.ZodArray<typeof AllowedWebsiteSchemaIPG>>;
    rpaModules: z.ZodDefault<z.ZodArray<typeof RpaModuleNameSchema>>;
}> = z.object({
    ipg_id: NumericIpgSystemIdSchema,
    name: z.string(),
    urlPattern: UrlPatternStringSchema,
    workflow_prefix: z.string(),
    allowedWebsites: z.array(AllowedWebsiteSchemaIPG).default([]),
    rpaModules: z.array(RpaModuleNameSchema).default([]),
});

export const IpgConfigWithGatewayDefaultsSchema: z.ZodObject<{
    ipg_id: typeof NumericIpgSystemIdSchema;
    name: z.ZodString;
    urlPattern: typeof UrlPatternStringSchema;
    workflow_prefix: z.ZodString;
    allowedWebsites: z.ZodDefault<z.ZodArray<typeof AllowedWebsiteSchemaIPG>>;
    rpaModules: z.ZodDefault<z.ZodArray<typeof RpaModuleNameSchema>>;
    default_api_url: z.ZodOptional<z.ZodString>;
    default_channel: z.ZodOptional<z.ZodString>;
}> = BaseIpgConfigSchema.extend({
    default_api_url: z.string().url().optional(),
    default_channel: z.string().trim().min(1).optional(),
});

export interface SambaCredentialDefinitionMetadata {
    ipx_credential_definition_uuid: string;
    system_id: ThirdPartySystemId;
    credential_definition_key: string;
    trigger_url_patterns: UrlPatternString[];
}

export interface SambaCredentialMetadata {
    credential_uuid: string;
    account_id: string;
    definition: SambaCredentialDefinitionMetadata;
}

export const SambaCredentialDefinitionMetadataSchema:
    z.ZodType<SambaCredentialDefinitionMetadata> = z.object({
    ipx_credential_definition_uuid: z.string().uuid(),
    system_id: ThirdPartySystemIdSchema,
    credential_definition_key: z.string(),
    trigger_url_patterns: UrlPatternStringArraySchema,
});

export const SambaCredentialMetadataSchema: z.ZodType<SambaCredentialMetadata> =
    z.object({
        credential_uuid: z.string().uuid(),
        account_id: z.string(),
        definition: SambaCredentialDefinitionMetadataSchema,
    });

export interface SambaConfig {
    allowPromoteSessionCookie: boolean;
    unmanagedAllowedWebsites: UnmanagedAllowedWebsite[];
    credentialMetadata: SambaCredentialMetadata[];
}

export interface StoredSambaConfig {
    allowPromoteSessionCookie?: boolean;
    credentialMetadata?: SambaCredentialMetadata[];
}

// Zod schema for SambaConfig
export const SambaConfigSchema: z.ZodType<SambaConfig> = z.object({
    allowPromoteSessionCookie: z.boolean().default(false),
    unmanagedAllowedWebsites: z.array(AllowedWebsiteSchemaUnmanaged).default([]),
    credentialMetadata: z.array(SambaCredentialMetadataSchema).default([]),
});

export const StoredSambaConfigSchema: z.ZodType<StoredSambaConfig> =
    z.object({
        allowPromoteSessionCookie: z.boolean().optional(),
        credentialMetadata: z.array(SambaCredentialMetadataSchema).optional(),
    });

export const StoredSambaConfigWriteSchema: z.ZodType<StoredSambaConfig> =
    z.object({
        allowPromoteSessionCookie: z.boolean().optional(),
        credentialMetadata: z.array(SambaCredentialMetadataSchema).optional(),
    }).strict();

export const CustomerPortalDeliveryChannel = {
    EMAIL: 'EMAIL' as const,
    SMS: 'SMS' as const,
} as const;
export type CustomerPortalDeliveryChannel =
    (typeof CustomerPortalDeliveryChannel)[keyof typeof CustomerPortalDeliveryChannel];

export const CustomerPortalDeliveryChannelSchema = z.enum(CustomerPortalDeliveryChannel);

export const E164PhoneNumberSchema = z.string().regex(/^\+[1-9]\d{1,14}$/);
export type E164PhoneNumber = z.infer<typeof E164PhoneNumberSchema>;

export const PaymentRequestDocumentPolicySchema = z.object({
    allowUploadedDocuments: z.boolean().default(true),
    allowGeneratedDocuments: z.boolean().default(true),
    requireDocument: z.boolean().default(false),
});
export type PaymentRequestDocumentPolicy = z.infer<typeof PaymentRequestDocumentPolicySchema>;

export const PaymentRequestTemplateSelectionSchema = z.object({
    paymentRequestDetailsTypeId: z.string().min(1),
    paymentRequestDetailsTypeVersion: z.number().int().nonnegative(),
    documentTemplateUuid: z.string().uuid(),
});
export type PaymentRequestTemplateSelection = z.infer<
    typeof PaymentRequestTemplateSelectionSchema
>;

export interface CustomerPortalTwilioSmsDeliveryConfigEnabled {
    readonly enabled: true;
    readonly fromPhoneNumber?: E164PhoneNumber | undefined;
    readonly messagingServiceSid?: string | undefined;
}

export interface CustomerPortalTwilioSmsDeliveryConfigDisabled {
    readonly enabled: false;
    readonly fromPhoneNumber?: E164PhoneNumber | undefined;
    readonly messagingServiceSid?: string | undefined;
}

export type CustomerPortalTwilioSmsDeliveryConfig =
    | CustomerPortalTwilioSmsDeliveryConfigEnabled
    | CustomerPortalTwilioSmsDeliveryConfigDisabled;

export const CustomerPortalTwilioSmsDeliveryConfigEnabledSchema: z.ZodObject<{
    enabled: z.ZodLiteral<true>;
    fromPhoneNumber: z.ZodOptional<typeof E164PhoneNumberSchema>;
    messagingServiceSid: z.ZodOptional<z.ZodString>;
}> = z
    .object({
        enabled: z.literal(true),
        fromPhoneNumber: E164PhoneNumberSchema.optional(),
        messagingServiceSid: z.string().min(1).optional(),
    })
    .refine(
        (value) =>
            (value.fromPhoneNumber !== undefined) !==
            (value.messagingServiceSid !== undefined),
        {
            message:
                'Enabled Twilio SMS config requires exactly one sender field.',
        }
    );

export const CustomerPortalTwilioSmsDeliveryConfigDisabledSchema: z.ZodObject<{
    enabled: z.ZodLiteral<false>;
    fromPhoneNumber: z.ZodOptional<typeof E164PhoneNumberSchema>;
    messagingServiceSid: z.ZodOptional<z.ZodString>;
}> = z.object({
    enabled: z.literal(false),
    fromPhoneNumber: E164PhoneNumberSchema.optional(),
    messagingServiceSid: z.string().min(1).optional(),
});

export const CustomerPortalTwilioSmsDeliveryConfigSchema: z.ZodDiscriminatedUnion<
    [
        typeof CustomerPortalTwilioSmsDeliveryConfigEnabledSchema,
        typeof CustomerPortalTwilioSmsDeliveryConfigDisabledSchema,
    ],
    'enabled'
> = z.discriminatedUnion('enabled', [
    CustomerPortalTwilioSmsDeliveryConfigEnabledSchema,
    CustomerPortalTwilioSmsDeliveryConfigDisabledSchema,
]);

export const CustomerPortalSendgridEmailDeliveryConfigSchema = z.object({
    enabled: z.boolean().default(false),
    fromEmail: z.string().email(),
    fromName: z.string().min(1).optional(),
    replyToEmail: z.string().email().optional(),
});
export type CustomerPortalSendgridEmailDeliveryConfig = z.infer<
    typeof CustomerPortalSendgridEmailDeliveryConfigSchema
>;

export const CustomerPortalPaymentRequestsConfigSchema = z.object({
    enabled: z.boolean().default(false),
    allowedDeliveryChannels: z.array(CustomerPortalDeliveryChannelSchema).default([]),
    documentPolicy: PaymentRequestDocumentPolicySchema.optional(),
    templateSelections: z.array(PaymentRequestTemplateSelectionSchema).default([]),
    twilioSms: CustomerPortalTwilioSmsDeliveryConfigSchema.optional(),
    sendgridEmail: CustomerPortalSendgridEmailDeliveryConfigSchema.optional(),
});
export type CustomerPortalPaymentRequestsConfig = z.infer<
    typeof CustomerPortalPaymentRequestsConfigSchema
>;

export const CustomerPortalConfigSchema = z.object({
    paymentRequests: CustomerPortalPaymentRequestsConfigSchema.optional(),
});
export type CustomerPortalConfig = z.infer<typeof CustomerPortalConfigSchema>;

export const IpgSystemIdSchema = z.enum(IpgSystemIdKeyList);
export const IpwSystemIdSchema = z.enum(IpwSystemIdKeyList);

export type AllowedWebsiteCustomization = Pick<
    AllowedWebsiteBase,
    | 'allowedUrlPatterns'
    | 'sessionPersistence'
    | 'promoteSessionCookies'
    | 'urlPatternsRules'
    | 'fetchDefaultAction'
    | 'iframeUrlPatterns'
    | 'iframeDefaultAction'
>;

export const AllowedWebsiteCustomizationSchema: z.ZodType<AllowedWebsiteCustomization> =
    z
        .object({
            allowedUrlPatterns: UrlPatternStringArraySchema,
            sessionPersistence: SessionPersistenceTypeSchema,
            promoteSessionCookies: z.boolean().default(false),
            urlPatternsRules: z.array(UrlPatternsRuleSchema).default([]),
            fetchDefaultAction: UrlPatternsRuleActionSchema.default(
                UrlPatternsRuleAction.ALLOW
            ),
            iframeUrlPatterns: z.array(UrlPatternsRuleSchema).default([]),
            iframeDefaultAction: UrlPatternsRuleActionSchema.default(
                UrlPatternsRuleAction.ALLOW
            ),
        })
        .strict();

export type IpwReconciliationCustomization =
    IpwConfigBase['reconciliation'];

export const IpwReconciliationCustomizationSchema: z.ZodType<IpwReconciliationCustomization> =
    IpwConfigBaseSchema.shape.reconciliation;

export type IpwConfigBaseCustomization = {
    ipw_system_id: IpwConfigBase['ipw_system_id'];
    urlPattern: IpwConfigBase['urlPattern'];
    allowedWebsites: AllowedWebsiteCustomization[];
    reconciliation?: IpwReconciliationCustomization;
};

export type IpwConfigDealertrackCustomization = IpwConfigBaseCustomization & {
    ipw_system_id: typeof IpwSystemIdKey.DEALERTRACK;
};

export type IpwConfigPcpartpickerCustomization = IpwConfigBaseCustomization & {
    ipw_system_id: typeof IpwSystemIdKey.PCPARTPICKER;
};

export type IpwConfigCustomization =
    | IpwConfigDealertrackCustomization
    | IpwConfigPcpartpickerCustomization;

export const IpwConfigCustomizationBaseSchema: z.ZodObject<{
    ipw_system_id: z.ZodType<IpwSystemIdKey>;
    urlPattern: typeof UrlPatternStringSchema;
    allowedWebsites: z.ZodArray<typeof AllowedWebsiteCustomizationSchema>;
    reconciliation: z.ZodOptional<typeof IpwReconciliationCustomizationSchema>;
}> = z.object({
    ipw_system_id: z.enum(IpwSystemIdKeyList),
    urlPattern: UrlPatternStringSchema,
    allowedWebsites: z.array(AllowedWebsiteCustomizationSchema),
    reconciliation: IpwReconciliationCustomizationSchema.optional(),
});

export const IpwConfigDealertrackCustomizationSchema: z.ZodType<IpwConfigDealertrackCustomization> =
    IpwConfigCustomizationBaseSchema.extend({
        ipw_system_id: z.literal(IpwSystemIdKey.DEALERTRACK),
    }).strict();

export const IpwConfigPcpartpickerCustomizationSchema: z.ZodType<IpwConfigPcpartpickerCustomization> =
    IpwConfigCustomizationBaseSchema.extend({
        ipw_system_id: z.literal(IpwSystemIdKey.PCPARTPICKER),
    }).strict();

export const IpwConfigCustomizationSchema: z.ZodType<IpwConfigCustomization> =
    z.union([
        IpwConfigDealertrackCustomizationSchema,
        IpwConfigPcpartpickerCustomizationSchema,
    ]);

export type IpgConfigBaseCustomization = {
    ipg_id: BaseIpgConfig['ipg_id'];
    urlPattern: BaseIpgConfig['urlPattern'];
    allowedWebsites: AllowedWebsiteCustomization[];
};

export type IpwConfigCustomizationMap = Partial<
    Record<IpwSystemIdKey, IpwConfigCustomization>
>;

export const IpwConfigCustomizationMapSchema: z.ZodType<IpwConfigCustomizationMap> =
    z.partialRecord(IpwSystemIdSchema, IpwConfigCustomizationSchema);

export type UpdateIpwConfigCustomizationMap = Partial<
    Record<IpwSystemIdKey, IpwConfigCustomization | null>
>;

export const UpdateIpwConfigCustomizationMapSchema: z.ZodType<UpdateIpwConfigCustomizationMap> =
    z.partialRecord(
        IpwSystemIdSchema,
        IpwConfigCustomizationSchema.nullable()
    );

export const USStateKeyList = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
    'DC',
] as const;
export const USStateSchema = z.enum(USStateKeyList);
export type USState = z.infer<typeof USStateSchema>;

export const CanadaProvinceKeyList = [
  'AB', // Alberta
  'BC', // British Columbia
  'MB', // Manitoba
  'NB', // New Brunswick
  'NL', // Newfoundland and Labrador
  'NS', // Nova Scotia
  'NT', // Northwest Territories
  'NU', // Nunavut
  'ON', // Ontario
  'PE', // Prince Edward Island
  'QC', // Quebec
  'SK', // Saskatchewan
  'YT', // Yukon
];
export const CanadaProvinceSchema = z.enum(CanadaProvinceKeyList);
export type CanadaProvince = z.infer<typeof CanadaProvinceSchema>;


export const USZipCode5Schema = z.string().regex(/^\d{5}$/);
export const USZipCode5Plus4Schema = z.string().regex(/^\d{5}-\d{4}$/);
export const USZipCodeSchema = z.union([USZipCode5Schema, USZipCode5Plus4Schema]);
export type USZipCode = z.infer<typeof USZipCodeSchema>;

export const normalizePhoneNumber = (value: string): string => value.replace(/[^0-9]/g, '');

export const formatPhoneNumber = (value: string): string => {
    const normalizedDigits: string = normalizePhoneNumber(value);
    const digits: string =
        normalizedDigits.length >= 11 && normalizedDigits.startsWith('1')
            ? normalizedDigits.slice(1, 11)
            : normalizedDigits.slice(0, 10);
    if (digits.length === 0) {
        return '';
    }
    if (digits.length <= 3) {
        return `(${digits}`;
    }
    if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const normalizeNorthAmericanPhoneNumberToE164 = (value: string): string => {
    const trimmedValue: string = value.trim();
    const normalizedDigits: string = normalizePhoneNumber(trimmedValue);

    if (trimmedValue.length === 0) {
        return '';
    }

    if (trimmedValue.startsWith('+')) {
        return normalizedDigits.length === 0 ? '' : `+${normalizedDigits}`;
    }

    if (normalizedDigits.length === 10) {
        return `+1${normalizedDigits}`;
    }

    if (normalizedDigits.length === 11 && normalizedDigits.startsWith('1')) {
        return `+${normalizedDigits}`;
    }

    return trimmedValue;
};

export const isNormalizedPhoneNumberValid = (value: string): boolean =>
    normalizePhoneNumber(value).length === 10;

export const getPhoneNumberValidationMessage = (value: string): string | null => {
    const trimmedValue: string = value.trim();
    if (trimmedValue.length === 0) {
        return null;
    }
    if (!isNormalizedPhoneNumberValid(trimmedValue)) {
        return 'Phone number must be 10 digits.';
    }
    return null;
};

export const normalizeUSZipCode = (value: string): string => {
    const digits: string = value.replace(/[^0-9]/g, '').slice(0, 9);
    if (digits.length <= 5) {
        return digits;
    }
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export const CanadaPostalCodeSchema = z.string().regex(
  /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i,
  "Invalid Canadian Postal Code (Expected Format: A1A 1A1)"
);
export type CanadaPostalCode = z.infer<typeof CanadaPostalCodeSchema>;

export const normalizeCanadaPostalCode = (value: string): string => {
    const normalizedChars: string[] = [];
    const upperValue: string = value.toUpperCase();
    for (const char of upperValue) {
        if (normalizedChars.length >= 6) {
            break;
        }
        const position: number = normalizedChars.length;
        const expectsLetter: boolean = position % 2 === 0;
        if (expectsLetter) {
            if (!/[A-Z]/.test(char)) {
                continue;
            }
            normalizedChars.push(char);
            continue;
        }
        if (!/[0-9]/.test(char)) {
            continue;
        }
        normalizedChars.push(char);
    }
    const compact: string = normalizedChars.join('');
    if (compact.length <= 3) {
        return compact;
    }
    return `${compact.slice(0, 3)} ${compact.slice(3)}`;
};

export const AddressUSSchema = z.object({
    country: z.literal('US'),
    street: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: USStateSchema,
    zip: USZipCodeSchema,
});

export type AddressUS = z.infer<typeof AddressUSSchema>;

export const AddressCanadaSchema = z.object({
    country: z.literal('CA'),
    street: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    province: CanadaProvinceSchema,
    postal_code: CanadaPostalCodeSchema,
});

export type AddressCanada = z.infer<typeof AddressCanadaSchema>;

export type Address = AddressCanada | AddressUS;

export const AddressDiscriminatedUnionSchema: z.ZodType<Address> = z.discriminatedUnion('country', [
    AddressCanadaSchema,
    AddressUSSchema,
]);

export const AddressSchema: z.ZodType<Address> = AddressDiscriminatedUnionSchema;

const isNonEmptyAddressValue = (value: string): boolean => value.trim().length > 0;

export function isAddressValid(address: Address): boolean {
    if (
        !isNonEmptyAddressValue(address.street) ||
        !isNonEmptyAddressValue(address.city)
    ) {
        return false;
    }

    switch (address.country) {
        case 'US':
            return isNonEmptyAddressValue(address.zip);
        case 'CA':
            return isNonEmptyAddressValue(address.postal_code);
    }
}

export type PartialAddressUS = Partial<AddressUS> & {
    country: 'US';
};

export const PartialAddressUSSchema: z.ZodType<PartialAddressUS> =
    AddressUSSchema.partial().extend({
        country: z.literal('US'),
    });

export type PartialAddressCanada = Partial<AddressCanada> & {
    country: 'CA';
};

export const PartialAddressCanadaSchema: z.ZodType<PartialAddressCanada> =
    AddressCanadaSchema.partial().extend({
        country: z.literal('CA'),
    });

export type BusinessDetailsCore = {
    legalEntityName: string;
    dba?: string;
    department?: string;
    main_phone_number?: string;
}

export const BusinessDetailsCoreSchema = z.object({
    legalEntityName: z.string(),
    dba: z.string().optional(),
    department: z.string().optional(),
    main_phone_number: z.string().optional(),
})

export type BusinessDetailsUS = BusinessDetailsCore & {
    country: 'US';
    defaultCurrency: 'USD',
    address?: PartialAddressUS;
};

export const BusinessDetailsUSSchema = BusinessDetailsCoreSchema.extend({
    country: z.literal('US'),
    defaultCurrency: z.literal('USD').default('USD'),
    address: PartialAddressUSSchema.optional(),
});

export type BusinessDetailsCanada = BusinessDetailsCore & {
    country: 'CA';
    defaultCurrency: 'CAD',
    address?: PartialAddressCanada;
};

export const BusinessDetailsCanadaSchema = BusinessDetailsCoreSchema.extend({
    country: z.literal('CA'),
    defaultCurrency: z.literal('CAD').default('CAD'),
    address: PartialAddressCanadaSchema.optional(),
});

export type BusinessDetails = BusinessDetailsUS | BusinessDetailsCanada;

export const BusinessDetailsSchema = z.discriminatedUnion('country', [
    BusinessDetailsUSSchema,
    BusinessDetailsCanadaSchema,
]);

export const BinLookupBackend = {
    VALOR: 'VALOR',
    BINLOOKUPAPI: 'BINLOOKUPAPI',
} as const;

export type BinLookupBackend = (typeof BinLookupBackend)[keyof typeof BinLookupBackend];

export interface ValorBinLookupServiceConfig {
    readonly bin_lookup_backend: typeof BinLookupBackend.VALOR;
    readonly credential_uuid: string;
}

export interface BinLookupApiServiceConfig {
    readonly bin_lookup_backend: typeof BinLookupBackend.BINLOOKUPAPI;
}

// Discriminator: bin_lookup_backend
export type BinLookupServiceConfig =
    | ValorBinLookupServiceConfig
    | BinLookupApiServiceConfig;

export const BinLookupServiceConfigSchema: z.ZodType<BinLookupServiceConfig> =
    z.discriminatedUnion('bin_lookup_backend', [
        z.object({
            bin_lookup_backend: z.literal(BinLookupBackend.VALOR),
            credential_uuid: z.string().uuid(),
        }),
        z.object({
            bin_lookup_backend: z.literal(BinLookupBackend.BINLOOKUPAPI),
        }),
    ]);

export type PlatformPaymentMethods = {
    terminal: boolean;
    card: boolean;
    ach: boolean;
    e_invoice: boolean;
}

export const PlatformPaymentMethodsSchema: z.ZodType<PlatformPaymentMethods> = z.object({
    terminal: z.boolean().default(false),
    card: z.boolean().default(false),
    ach: z.boolean().default(false),
    e_invoice: z.boolean().default(false),
});

export type PlatformFeatures = {
    payment_methods: PlatformPaymentMethods;
    customerPortal: CustomerPortalConfig | null;
}

export const PlatformFeaturesSchema: z.ZodType<PlatformFeatures> = z.object({
    payment_methods: PlatformPaymentMethodsSchema.default({
        terminal: false,
        card: false,
        ach: false,
        e_invoice: false,
    }),
    customerPortal: CustomerPortalConfigSchema.nullable().default(null),
});

export type IpsConfig = {
    automaticCustomerDetailsDetectionEnabled: boolean;
    customerDetailsDetectionButtonEnabled: boolean;
    addressRequiredForCardPayment?: boolean;
    enablePaymentsInPillar?: boolean;
}

export const ResolvedDefaultAutomaticCustomerDetailsDetectionEnabled: boolean = false;
export const ResolvedDefaultCustomerDetailsDetectionButtonEnabled: boolean = false;
export const ResolvedDefaultAddressRequiredForCardPayment: boolean = false;

export const ResolvedDefaultIpsConfig: IpsConfig = {
    automaticCustomerDetailsDetectionEnabled:
        ResolvedDefaultAutomaticCustomerDetailsDetectionEnabled,
    customerDetailsDetectionButtonEnabled:
        ResolvedDefaultCustomerDetailsDetectionButtonEnabled,
};

export const IpsConfigSchema: z.ZodObject<{
    automaticCustomerDetailsDetectionEnabled: z.ZodDefault<z.ZodBoolean>;
    customerDetailsDetectionButtonEnabled: z.ZodDefault<z.ZodBoolean>;
    addressRequiredForCardPayment: z.ZodOptional<z.ZodBoolean>;
    enablePaymentsInPillar: z.ZodOptional<z.ZodBoolean>;
}> = z.object({
    automaticCustomerDetailsDetectionEnabled: z.boolean().default(
        ResolvedDefaultAutomaticCustomerDetailsDetectionEnabled
    ),
    customerDetailsDetectionButtonEnabled: z.boolean().default(
        ResolvedDefaultCustomerDetailsDetectionButtonEnabled
    ),
    addressRequiredForCardPayment: z.boolean().optional(),
    enablePaymentsInPillar: z.boolean().optional(),
});

export type PlatformConfig = {
    features: PlatformFeatures;
    payment_request_email_cc: string[];
    require2FA: boolean;
}

export const PlatformConfigSchema: z.ZodType<PlatformConfig> = z.object({
    features: PlatformFeaturesSchema,
    payment_request_email_cc: z.array(z.string().email()).default([]),
    require2FA: z.boolean().default(false),
});


export type ProcessorConfig = {
    reporting_dashboard?: IpgAllowedWebsite,
}

export const ProcessorConfigSchema: z.ZodType<ProcessorConfig> = z.object({
    reporting_dashboard: AllowedWebsiteSchemaIPG.optional()
})

export const ProcessorConfigStorageV1Schema: z.ZodType<ProcessorConfig> =
    z.preprocess(normalizeLegacyProcessorConfigSystemIds, ProcessorConfigSchema);

export const ProcessorConfigStorageVersion = {
    V2: 2,
} as const;

export type ProcessorConfigStorageVersion =
    (typeof ProcessorConfigStorageVersion)[keyof typeof ProcessorConfigStorageVersion];

export type ProcessorConfigStorageV1 = ProcessorConfig;

export interface ProcessorConfigStorageV2 {
    readonly processor_config_version:
        typeof ProcessorConfigStorageVersion.V2;
    readonly config: ProcessorConfig;
}

export type StoredProcessorConfig =
    | ProcessorConfigStorageV1
    | ProcessorConfigStorageV2;

export const ProcessorConfigStorageVersionSchema:
    z.ZodType<ProcessorConfigStorageVersion> =
    z.literal(ProcessorConfigStorageVersion.V2);

export const ProcessorConfigStorageV2Schema:
    z.ZodType<ProcessorConfigStorageV2> =
    z.object({
        processor_config_version: ProcessorConfigStorageVersionSchema,
        config: ProcessorConfigSchema,
    }).strict();

export const StoredProcessorConfigStorageV1Schema:
    z.ZodType<ProcessorConfigStorageV1> =
    z.preprocess(
        normalizeStoredMerchantConfigNumericLeaves,
        ProcessorConfigStorageV1Schema
    );

export const StoredProcessorConfigStorageV2Schema:
    z.ZodType<ProcessorConfigStorageV2> =
    z.preprocess(
        normalizeStoredMerchantConfigNumericLeaves,
        ProcessorConfigStorageV2Schema
    );

export const ProcessorConfigVersionedObjectMarkerSchema:
    z.ZodType<{ readonly processor_config_version: unknown }> =
    z.object({
        processor_config_version: z.unknown(),
    }).passthrough();

export const StoredProcessorConfigSchema:
    z.ZodType<StoredProcessorConfig> =
    z.union([
        StoredProcessorConfigStorageV2Schema,
        StoredProcessorConfigStorageV1Schema,
    ]);

export function normalizeProcessorConfigStorageV1(
    config: ProcessorConfigStorageV1
): ProcessorConfig {
    return config;
}

export function normalizeProcessorConfigStorageV2(
    config: ProcessorConfigStorageV2
): ProcessorConfig {
    return config.config;
}

export const ProcessorConfigStorageContract = defineStoredJsonContract({
    contractName: 'ProcessorConfig',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredProcessorConfigStorageV1Schema',
            schema: StoredProcessorConfigStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['processor_config_version'],
                },
            ],
            normalize: normalizeProcessorConfigStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredProcessorConfigStorageV2Schema',
            schema: StoredProcessorConfigStorageV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['processor_config_version'],
                    markerValue: ProcessorConfigStorageVersion.V2,
                },
            ],
            normalize: normalizeProcessorConfigStorageV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredProcessorConfigParser:
    z.ZodType<ProcessorConfig> =
    buildStoredJsonValueParser(ProcessorConfigStorageContract);

export const ProcessorConfigStorageSchema: z.ZodType<ProcessorConfig> =
    StoredProcessorConfigParser;

export function serializeProcessorConfigV2(
    config: ProcessorConfig
): ProcessorConfigStorageV2 {
    return {
        processor_config_version: ProcessorConfigStorageVersion.V2,
        config: ProcessorConfigSchema.parse(config),
    };
}

export type MerchantUserRoleAssignmentConfig = {
    organizationKind: 'MERCHANT',
    defaultPhysicalTerminalDevice?: string, // UUID
    defaultVirtualTerminalDevice?: string, // UUID
    lastPhysicalTerminalDevice?: string, // UUID
    lastVirtualTerminalDevice?: string, // UUID
}
export type IsoUserRoleAssignmentConfig = {
    organizationKind: 'ISO'
}
export type CustomerUserRoleAssignmentConfig = {
    organizationKind: 'CUSTOMER'
}
export type UserRoleAssignmentConfig = MerchantUserRoleAssignmentConfig | IsoUserRoleAssignmentConfig | CustomerUserRoleAssignmentConfig;

export type MerchantUserRoleAssignmentConfigPatch = {
    organizationKind?: 'MERCHANT';
    defaultPhysicalTerminalDevice?: string;
    defaultVirtualTerminalDevice?: string;
    lastPhysicalTerminalDevice?: string;
    lastVirtualTerminalDevice?: string;
};
export type IsoUserRoleAssignmentConfigPatch = {
    organizationKind?: 'ISO';
};
export type CustomerUserRoleAssignmentConfigPatch = {
    organizationKind?: 'CUSTOMER';
};
export type UserRoleAssignmentConfigPatch =
    | MerchantUserRoleAssignmentConfigPatch
    | IsoUserRoleAssignmentConfigPatch
    | CustomerUserRoleAssignmentConfigPatch;

export const MerchantUserRoleAssignmentConfigSchema: z.ZodObject<{
    organizationKind: z.ZodLiteral<'MERCHANT'>;
    defaultPhysicalTerminalDevice: z.ZodOptional<z.ZodString>;
    defaultVirtualTerminalDevice: z.ZodOptional<z.ZodString>;
    lastPhysicalTerminalDevice: z.ZodOptional<z.ZodString>;
    lastVirtualTerminalDevice: z.ZodOptional<z.ZodString>;
}> = z.object({
    organizationKind: z.literal('MERCHANT'),
    defaultPhysicalTerminalDevice: z.string().uuid().optional(),
    defaultVirtualTerminalDevice: z.string().uuid().optional(),
    lastPhysicalTerminalDevice: z.string().uuid().optional(),
    lastVirtualTerminalDevice: z.string().uuid().optional(),
});
export const IsoUserRoleAssignmentConfigSchema: z.ZodObject<{
    organizationKind: z.ZodLiteral<'ISO'>;
}> = z.object({
    organizationKind: z.literal('ISO'),
});
export const CustomerUserRoleAssignmentConfigSchema: z.ZodObject<{
    organizationKind: z.ZodLiteral<'CUSTOMER'>;
}> = z.object({
    organizationKind: z.literal('CUSTOMER'),
});
export const UserRoleAssignmentConfigSchema: z.ZodDiscriminatedUnion<
    [
        typeof MerchantUserRoleAssignmentConfigSchema,
        typeof IsoUserRoleAssignmentConfigSchema,
        typeof CustomerUserRoleAssignmentConfigSchema,
    ],
    'organizationKind'
> = z.discriminatedUnion('organizationKind', [
    MerchantUserRoleAssignmentConfigSchema,
    IsoUserRoleAssignmentConfigSchema,
    CustomerUserRoleAssignmentConfigSchema,
]);

export const MerchantUserRoleAssignmentConfigPatchSchema: z.ZodObject<{
    organizationKind: z.ZodOptional<z.ZodLiteral<'MERCHANT'>>;
    defaultPhysicalTerminalDevice: z.ZodOptional<z.ZodString>;
    defaultVirtualTerminalDevice: z.ZodOptional<z.ZodString>;
    lastPhysicalTerminalDevice: z.ZodOptional<z.ZodString>;
    lastVirtualTerminalDevice: z.ZodOptional<z.ZodString>;
}> = z
    .object({
        organizationKind: z.literal('MERCHANT').optional(),
        defaultPhysicalTerminalDevice: z.string().uuid().optional(),
        defaultVirtualTerminalDevice: z.string().uuid().optional(),
        lastPhysicalTerminalDevice: z.string().uuid().optional(),
        lastVirtualTerminalDevice: z.string().uuid().optional(),
    })
    .strict();

export const IsoUserRoleAssignmentConfigPatchSchema: z.ZodObject<{
    organizationKind: z.ZodOptional<z.ZodLiteral<'ISO'>>;
}> = z
    .object({
        organizationKind: z.literal('ISO').optional(),
    })
    .strict();

export const CustomerUserRoleAssignmentConfigPatchSchema: z.ZodObject<{
    organizationKind: z.ZodOptional<z.ZodLiteral<'CUSTOMER'>>;
}> = z
    .object({
        organizationKind: z.literal('CUSTOMER').optional(),
    })
    .strict();

export const UserRoleAssignmentConfigPatchSchema: z.ZodUnion<
    [
        typeof MerchantUserRoleAssignmentConfigPatchSchema,
        typeof IsoUserRoleAssignmentConfigPatchSchema,
        typeof CustomerUserRoleAssignmentConfigPatchSchema,
    ]
> = z.union([
    MerchantUserRoleAssignmentConfigPatchSchema,
    IsoUserRoleAssignmentConfigPatchSchema,
    CustomerUserRoleAssignmentConfigPatchSchema,
]);

export type MerchantConfig = {
    requires_password_change: boolean;
    businessDetails: BusinessDetails | null;
    ipg: Partial<Record<IpgSystemIdKey, IpgConfig>>;
    ipw: Partial<Record<IpwSystemIdKey, IpwConfig>>;
    rbs: RbsConfig;
    ips: IpsConfig;
    binLookupService: BinLookupServiceConfig | null;
    processorConfig: ProcessorConfig[];
    sambaConfig: SambaConfig;
    platformConfig: PlatformConfig;
    userRoleAssignmentConfig: UserRoleAssignmentConfig | null;
}

type IsTuple<T extends readonly unknown[]> = number extends T['length'] ? false : true;

export type DeepPartial<T> =
    T extends (...args: never[]) => unknown
        ? T
        : T extends readonly unknown[]
            ? IsTuple<T> extends true
                ? { [K in keyof T]?: DeepPartial<T[K]> }
                : T extends unknown[]
                    ? Array<DeepPartial<T[number]>>
                    : ReadonlyArray<DeepPartial<T[number]>>
            : T extends object
                ? { [K in keyof T]?: DeepPartial<T[K]> }
                : T | undefined;

export type MerchantConfigStorage = DeepPartial<
    Omit<MerchantConfig, 'ipg' | 'sambaConfig'>
> & {
    ipg?: IpgConfigStorageMap;
    sambaConfig?: StoredSambaConfig;
};

export type LegacyMerchantConfigStorage = DeepPartial<
    Omit<MerchantConfig, 'ipg' | 'sambaConfig'>
> & {
    ipg?: Partial<
        Record<IpgSystemIdKey, DeepPartial<LegacyIpgConfig>>
    >;
    sambaConfig?: StoredSambaConfig;
};

export const MerchantConfigStorageVersion = {
    V2: 2,
    V3: 3,
    V4: 4,
} as const;

export type MerchantConfigStorageV1 = LegacyMerchantConfigStorage;

export interface MerchantConfigStorageV2 {
    readonly merchant_config_version:
        typeof MerchantConfigStorageVersion.V2;
    readonly config: LegacyMerchantConfigStorage;
}

export interface MerchantConfigStorageV3 {
    readonly merchant_config_version:
        typeof MerchantConfigStorageVersion.V3;
    readonly config: Omit<MerchantConfigStorage, 'binLookupService'>;
}

export interface MerchantConfigStorageV4 {
    readonly merchant_config_version:
        typeof MerchantConfigStorageVersion.V4;
    readonly config: MerchantConfigStorage;
}

export type MerchantConfigInput = DeepPartial<MerchantConfig>;

type DeepOptionalObjectShape = Record<string, z.ZodTypeAny>;
type DeepOptionalUnionOptions = ReadonlyArray<z.ZodTypeAny>;
type DeepOptionalRecordKeySchema = z.ZodType<string | number | symbol>;

function isZodDefaultSchema(schema: z.ZodTypeAny): schema is z.ZodDefault<z.ZodTypeAny> {
    return schema instanceof z.ZodDefault;
}

function isZodOptionalSchema(schema: z.ZodTypeAny): schema is z.ZodOptional<z.ZodTypeAny> {
    return schema instanceof z.ZodOptional;
}

function isZodNullableSchema(schema: z.ZodTypeAny): schema is z.ZodNullable<z.ZodTypeAny> {
    return schema instanceof z.ZodNullable;
}

function isZodReadonlySchema(schema: z.ZodTypeAny): schema is z.ZodReadonly<z.ZodTypeAny> {
    return schema instanceof z.ZodReadonly;
}

function isZodObjectSchema(schema: z.ZodTypeAny): schema is z.ZodObject<DeepOptionalObjectShape> {
    return schema instanceof z.ZodObject;
}

function isZodArraySchema(schema: z.ZodTypeAny): schema is z.ZodArray<z.ZodTypeAny> {
    return schema instanceof z.ZodArray;
}

function isZodRecordSchema(
    schema: z.ZodTypeAny
): schema is z.ZodRecord<DeepOptionalRecordKeySchema, z.ZodTypeAny> {
    return schema instanceof z.ZodRecord;
}

function isZodUnionSchema(schema: z.ZodTypeAny): schema is z.ZodUnion<DeepOptionalUnionOptions> {
    return schema instanceof z.ZodUnion;
}

function isZodDiscriminatedUnionSchema(
    schema: z.ZodTypeAny
): schema is z.ZodDiscriminatedUnion<DeepOptionalUnionOptions, string> {
    return schema instanceof z.ZodDiscriminatedUnion;
}

function removeSchemaDefaults(schema: z.ZodTypeAny): z.ZodTypeAny {
    if (isZodDefaultSchema(schema)) {
        return removeSchemaDefaults(schema.removeDefault());
    }
    return schema;
}

function createUnionFromOptions(options: ReadonlyArray<z.ZodTypeAny>): z.ZodTypeAny {
    const [firstOption, secondOption, ...remainingOptions] = options;

    if (firstOption === undefined) {
        return z.never();
    }
    if (secondOption === undefined) {
        return firstOption;
    }
    return z.union([firstOption, secondOption, ...remainingOptions]);
}

function createDeepOptionalSchemaInternal(
    schema: z.ZodTypeAny,
    strictObjects: boolean
): z.ZodTypeAny {
    const unwrappedSchema: z.ZodTypeAny = removeSchemaDefaults(schema);

    if (isZodOptionalSchema(unwrappedSchema)) {
        return createDeepOptionalSchemaInternal(
            unwrappedSchema.unwrap(),
            strictObjects
        ).optional();
    }

    if (isZodNullableSchema(unwrappedSchema)) {
        return createDeepOptionalSchemaInternal(
            unwrappedSchema.unwrap(),
            strictObjects
        ).nullable().optional();
    }

    if (isZodReadonlySchema(unwrappedSchema)) {
        return createDeepOptionalSchemaInternal(
            unwrappedSchema.unwrap(),
            strictObjects
        ).readonly().optional();
    }

    if (isZodObjectSchema(unwrappedSchema)) {
        const nextShape: Record<string, z.ZodTypeAny> = {};
        const currentShape: Readonly<DeepOptionalObjectShape> = unwrappedSchema.shape;

        for (const shapeKey in currentShape) {
            const shapeValue: z.ZodTypeAny | undefined = currentShape[shapeKey];
            if (shapeValue === undefined) {
                continue;
            }
            nextShape[shapeKey] = createDeepOptionalSchemaInternal(
                shapeValue,
                strictObjects
            );
        }
        return strictObjects
            ? z.strictObject(nextShape).partial()
            : z.object(nextShape).partial();
    }

    if (isZodArraySchema(unwrappedSchema)) {
        return z.array(
            createDeepOptionalSchemaInternal(
                unwrappedSchema.element,
                strictObjects
            )
        ).optional();
    }

    if (isZodRecordSchema(unwrappedSchema)) {
        return z
            .record(
                unwrappedSchema.keyType,
                createDeepOptionalSchemaInternal(
                    unwrappedSchema.valueType,
                    strictObjects
                )
            )
            .optional();
    }

    if (isZodUnionSchema(unwrappedSchema)) {
        return createUnionFromOptions(
            unwrappedSchema.options.map((option) =>
                createDeepOptionalSchemaInternal(option, strictObjects)
            )
        ).optional();
    }

    if (isZodDiscriminatedUnionSchema(unwrappedSchema)) {
        return createUnionFromOptions(
            Array.from(unwrappedSchema.options).map((option) =>
                createDeepOptionalSchemaInternal(option, strictObjects)
            )
        ).optional();
    }

    return unwrappedSchema.optional();
}

function createDeepOptionalSchema<T>(schema: z.ZodType<T>): z.ZodType<DeepPartial<T>>;
function createDeepOptionalSchema(schema: z.ZodTypeAny): z.ZodTypeAny {
    return createDeepOptionalSchemaInternal(schema, false);
}

function createStrictDeepOptionalSchema<T>(
    schema: z.ZodType<T>
): z.ZodType<DeepPartial<T>>;
function createStrictDeepOptionalSchema(
    schema: z.ZodTypeAny
): z.ZodTypeAny {
    return createDeepOptionalSchemaInternal(schema, true);
}

const IpgConfigSchemaRegistry = createIpgConfigSchemaRegistry({
    urlPatternSchema: UrlPatternStringSchema,
    allowedWebsiteSchema: AllowedWebsiteSchemaIPG,
    allowedWebsiteCustomizationSchema:
        AllowedWebsiteCustomizationSchema,
    rpaModuleNameSchema: RpaModuleNameSchema,
    createDeepOptionalSchema: createStrictDeepOptionalSchema,
});

export const IpgConfigSchema: z.ZodType<IpgConfig> =
    IpgConfigSchemaRegistry.schema;

export const IpgConfigSchemaBySystemId =
    IpgConfigSchemaRegistry.schemaById;

export const IpgConfigStorageMapSchema:
    z.ZodType<MerchantConfigStorage['ipg']> =
    IpgConfigSchemaRegistry.storageMapSchema;

export const IpgConfigCustomizationSchema:
    z.ZodType<IpgConfigCustomization> =
    IpgConfigSchemaRegistry.customizationSchema;

export const IpgConfigCustomizationSchemaBySystemId =
    IpgConfigSchemaRegistry.customizationSchemaById;

export const IpgConfigCustomizationMapSchema:
    z.ZodType<IpgConfigCustomizationMap> =
    IpgConfigSchemaRegistry.customizationMapSchema;

export const UpdateIpgConfigCustomizationMapSchema:
    z.ZodType<UpdateIpgConfigCustomizationMap> =
    IpgConfigSchemaRegistry.updateCustomizationMapSchema;

export const MerchantConfigObjectSchema: z.ZodType<MerchantConfig> = z.object({
    requires_password_change: z.boolean().default(false),
    businessDetails: BusinessDetailsSchema.nullable(),
    ipg: z.partialRecord(IpgSystemIdSchema, IpgConfigSchema),
    ipw: z.partialRecord(IpwSystemIdSchema, IpwConfigSchema),
    rbs: RbsConfigSchema.default({ websites: [] }),
    ips: IpsConfigSchema,
    binLookupService: BinLookupServiceConfigSchema.nullable().default(null),
    processorConfig: z.array(ProcessorConfigSchema),
    sambaConfig: SambaConfigSchema,
    platformConfig: PlatformConfigSchema,
    userRoleAssignmentConfig: UserRoleAssignmentConfigSchema.nullable(),
});

function createResolvedDefaultMerchantConfig(): MerchantConfig {
    return {
        requires_password_change: false,
        businessDetails: null,
        ipg: {},
        ipw: {},
        rbs: {
            websites: [],
        },
        ips: {
            ...ResolvedDefaultIpsConfig,
        },
        binLookupService: null,
        processorConfig: [],
        sambaConfig: {
            allowPromoteSessionCookie: false,
            unmanagedAllowedWebsites: [],
            credentialMetadata: [],
        },
        platformConfig: {
            features: {
                payment_methods: {
                    terminal: false,
                    card: false,
                    ach: false,
                    e_invoice: false,
                },
                customerPortal: null,
            },
            payment_request_email_cc: [],
            require2FA: false,
        },
        userRoleAssignmentConfig: null,
    };
}

type ResolvedMerchantConfigPrimitive = boolean | number | string | null | undefined;

type ResolvedMerchantConfigObject = object;

type ResolvedMerchantConfigValue =
    | ResolvedMerchantConfigPrimitive
    | ResolvedMerchantConfigObject
    | ReadonlyArray<ResolvedMerchantConfigValue>;

interface ResolvedMerchantConfigRecord {
    [key: string]: ResolvedMerchantConfigValue;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
    return Object.prototype.toString.call(value) === '[object Object]';
}

const StoredMerchantConfigIntegerLeafKeys: ReadonlySet<string> = new Set([
    'merchant_config_version',
    'role_assignment_custom_config_version',
    'processor_config_version',
    'system_id',
    'ipg_id',
    'ipw_id',
    'paymentRequestDetailsTypeVersion',
]);

function normalizeStoredMerchantConfigNumericLeaves(value: unknown): unknown {
    if (StoredLosslessNumberSchema.safeParse(value).success) {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map((entry) =>
            normalizeStoredMerchantConfigNumericLeaves(entry)
        );
    }
    if (!isPlainRecord(value)) {
        return value;
    }

    const normalized: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
        const entry = value[key];
        if (StoredMerchantConfigIntegerLeafKeys.has(key)) {
            const conversionResult = convertStoredJsonNumberToBoundedNumber(
                entry,
                {
                    domain: `merchant-config.${key}`,
                    numericKind: 'safe-integer',
                    minimum: 0,
                    maximum: Number.MAX_SAFE_INTEGER,
                }
            );
            normalized[key] = conversionResult.success
                ? conversionResult.value
                : entry;
            continue;
        }
        normalized[key] = normalizeStoredMerchantConfigNumericLeaves(entry);
    }
    return normalized;
}

function createThirdPartySystemIdByKeyMap<SystemKey extends string>(
    systemKeys: readonly SystemKey[],
    systemIdsByKey: Readonly<Record<SystemKey, ThirdPartySystemId>>
): ReadonlyMap<string, ThirdPartySystemId> {
    const systemIdByKey = new Map<string, ThirdPartySystemId>();
    for (const systemKey of systemKeys) {
        systemIdByKey.set(systemKey, systemIdsByKey[systemKey]);
    }
    return systemIdByKey;
}

const IpgSystemIdByKey: ReadonlyMap<string, ThirdPartySystemId> =
    createThirdPartySystemIdByKeyMap(IpgSystemIdKeyList, IpgSystemId);

const IpwSystemIdByKey: ReadonlyMap<string, ThirdPartySystemId> =
    createThirdPartySystemIdByKeyMap(IpwSystemIdKeyList, IpwSystemId);

function normalizeLegacyAllowedWebsiteSystemId(
    value: unknown,
    systemId: ThirdPartySystemId
): unknown {
    if (!isPlainRecord(value)) {
        return value;
    }
    if ('system_id' in value) {
        return value;
    }
    return {
        ...value,
        system_id: systemId,
    };
}

function normalizeLegacyAllowedWebsiteArraySystemIds(
    value: unknown,
    systemId: ThirdPartySystemId
): unknown {
    if (!Array.isArray(value)) {
        return value;
    }
    return value.map((entry) =>
        normalizeLegacyAllowedWebsiteSystemId(entry, systemId)
    );
}

function normalizeLegacyIpxConfigSystemIds(
    value: unknown,
    systemIdByKey: ReadonlyMap<string, ThirdPartySystemId>
): unknown {
    if (!isPlainRecord(value)) {
        return value;
    }
    const normalizedConfigMap: Record<string, unknown> = {};
    for (const systemKey in value) {
        const systemConfig: unknown = value[systemKey];
        const systemId: ThirdPartySystemId | undefined =
            systemIdByKey.get(systemKey);
        if (systemId === undefined || !isPlainRecord(systemConfig)) {
            normalizedConfigMap[systemKey] = systemConfig;
            continue;
        }
        normalizedConfigMap[systemKey] = {
            ...systemConfig,
            allowedWebsites: normalizeLegacyAllowedWebsiteArraySystemIds(
                systemConfig.allowedWebsites,
                systemId
            ),
        };
    }
    return normalizedConfigMap;
}

function dropLegacyGenericIpwStorageConfig(value: unknown): unknown {
    if (!isPlainRecord(value)) {
        return value;
    }
    if (!(IpwSystemIdKey.GENERIC in value)) {
        return value;
    }

    const normalizedConfigMap: Record<string, unknown> = { ...value };
    delete normalizedConfigMap[IpwSystemIdKey.GENERIC];
    return normalizedConfigMap;
}

function stripLegacyStoredSambaConfigUnmanagedAllowedWebsites(value: unknown): unknown {
    if (!isPlainRecord(value)) {
        return value;
    }
    const { unmanagedAllowedWebsites: _unmanagedAllowedWebsites, ...storedSambaConfig } =
        value;
    return storedSambaConfig;
}

function legacyProcessorReportingDashboardSystemId(): ThirdPartySystemId {
    const firstIpgSystemKey = IpgSystemIdKeyList[0];
    if (firstIpgSystemKey === undefined) {
        throw new Error('No IPG system ids are available for legacy processor config.');
    }
    const systemId = IpgSystemIdByKey.get(firstIpgSystemKey);
    if (systemId === undefined) {
        throw new Error(
            `Missing IPG system id mapping for legacy processor config: ${firstIpgSystemKey}`
        );
    }
    return systemId;
}

function normalizeLegacyProcessorConfigSystemIds(value: unknown): unknown {
    if (!isPlainRecord(value)) {
        return value;
    }
    if (!('reporting_dashboard' in value)) {
        return value;
    }
    return {
        ...value,
        reporting_dashboard: normalizeLegacyAllowedWebsiteSystemId(
            value.reporting_dashboard,
            legacyProcessorReportingDashboardSystemId()
        ),
    };
}

function normalizeLegacyProcessorConfigListSystemIds(value: unknown): unknown {
    if (!Array.isArray(value)) {
        return value;
    }
    return value.map((processorConfig) =>
        normalizeLegacyProcessorConfigSystemIds(processorConfig)
    );
}

function normalizeLegacyMerchantConfigStorage(config: unknown): unknown {
    if (!isPlainRecord(config)) {
        return config;
    }

    const normalizedConfig: Record<string, unknown> = { ...config };
    if ('ipg' in config) {
        normalizedConfig.ipg = normalizeLegacyIpxConfigSystemIds(
            config.ipg,
            IpgSystemIdByKey
        );
    }
    if ('ipw' in config) {
        normalizedConfig.ipw = dropLegacyGenericIpwStorageConfig(
            normalizeLegacyIpxConfigSystemIds(config.ipw, IpwSystemIdByKey)
        );
    }
    if ('sambaConfig' in config) {
        normalizedConfig.sambaConfig =
            stripLegacyStoredSambaConfigUnmanagedAllowedWebsites(config.sambaConfig);
    }
    if ('processorConfig' in config) {
        normalizedConfig.processorConfig = normalizeLegacyProcessorConfigListSystemIds(
            config.processorConfig
        );
    }
    return normalizedConfig;
}

function normalizeLegacyMerchantConfigRuntime(config: unknown): unknown {
    if (!isPlainRecord(config)) {
        return config;
    }

    const normalizedConfig: Record<string, unknown> = { ...config };
    if ('ipg' in config) {
        normalizedConfig.ipg = normalizeLegacyIpgConfigMap(
            normalizeLegacyIpxConfigSystemIds(
                config.ipg,
                IpgSystemIdByKey
            )
        );
    }
    if ('ipw' in config) {
        normalizedConfig.ipw = normalizeLegacyIpxConfigSystemIds(
            config.ipw,
            IpwSystemIdByKey
        );
    }
    if ('processorConfig' in config) {
        normalizedConfig.processorConfig = normalizeLegacyProcessorConfigListSystemIds(
            config.processorConfig
        );
    }
    return normalizedConfig;
}

function addZodIssues(ctx: z.RefinementCtx, issues: readonly z.ZodIssue[]): void {
    issues.forEach((issue) => {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: issue.message,
            path: [...issue.path],
        });
    });
}

function parseUnwrappedCurrentSchema<T>(
    value: unknown,
    ctx: z.RefinementCtx,
    schema: z.ZodType<T>,
    versionField: string,
    wrapperName: string
): T {
    if (isPlainRecord(value) && versionField in value) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [versionField],
            message: `${wrapperName} is a storage wrapper and is not valid here.`,
        });
        return z.NEVER;
    }

    const parseResult = schema.safeParse(value);
    if (!parseResult.success) {
        addZodIssues(ctx, parseResult.error.issues);
        return z.NEVER;
    }
    return parseResult.data;
}

function validateIpxAllowedWebsiteSystemIds(
    ctx: z.RefinementCtx,
    pathPrefix: 'ipg' | 'ipw',
    configMap: Partial<
        Record<
            string,
            {
                allowedWebsites: AllowedWebsite[];
            }
        >
    >,
    systemIdByKey: ReadonlyMap<string, ThirdPartySystemId>
): void {
    for (const systemKey in configMap) {
        const systemConfig = configMap[systemKey];
        const expectedSystemId = systemIdByKey.get(systemKey);
        if (systemConfig === undefined || expectedSystemId === undefined) {
            continue;
        }
        for (const [websiteIndex, allowedWebsite] of
            systemConfig.allowedWebsites.entries()) {
            if (allowedWebsite.system_id === expectedSystemId) {
                continue;
            }
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [
                    pathPrefix,
                    systemKey,
                    'allowedWebsites',
                    websiteIndex,
                    'system_id',
                ],
                message: `Allowed website system_id must match ${systemKey}.`,
            });
        }
    }
}

function validateMerchantConfigNestedAllowedWebsiteSystemIds(
    config: MerchantConfig,
    ctx: z.RefinementCtx
): void {
    validateIpxAllowedWebsiteSystemIds(ctx, 'ipg', config.ipg, IpgSystemIdByKey);
    validateIpxAllowedWebsiteSystemIds(ctx, 'ipw', config.ipw, IpwSystemIdByKey);
}

function isResolvedMerchantConfigObject(
    value: ResolvedMerchantConfigValue
): value is ResolvedMerchantConfigObject {
    return (
        value !== null &&
        value !== undefined &&
        !Array.isArray(value) &&
        Object.prototype.toString.call(value) === '[object Object]'
    );
}

function toResolvedMerchantConfigRecord(
    value: ResolvedMerchantConfigObject
): ResolvedMerchantConfigRecord {
    // Safe bridge: these object branches come from MerchantConfig or
    // MerchantConfigStorage values validated by Zod within this module.
    // eslint-disable-next-line no-restricted-syntax
    return value as ResolvedMerchantConfigRecord;
}

function deepMergeResolvedMerchantConfigValue(
    baseValue: ResolvedMerchantConfigValue,
    overrideValue: ResolvedMerchantConfigValue
): ResolvedMerchantConfigValue {
    if (overrideValue === undefined) {
        return baseValue;
    }

    if (Array.isArray(overrideValue)) {
        return overrideValue;
    }

    if (
        isResolvedMerchantConfigObject(baseValue) &&
        isResolvedMerchantConfigObject(overrideValue)
    ) {
        const baseRecord: ResolvedMerchantConfigRecord =
            toResolvedMerchantConfigRecord(baseValue);
        const overrideRecord: ResolvedMerchantConfigRecord =
            toResolvedMerchantConfigRecord(overrideValue);
        const mergedRecord: ResolvedMerchantConfigRecord = {
            ...baseRecord,
        };
        for (const overrideKey in overrideRecord) {
            const overrideEntryValue = overrideRecord[overrideKey];
            const baseEntryValue = baseRecord[overrideKey];
            const mergedEntryValue = deepMergeResolvedMerchantConfigValue(
                baseEntryValue,
                overrideEntryValue
            );
            if (mergedEntryValue !== undefined) {
                mergedRecord[overrideKey] = mergedEntryValue;
            }
        }
        return mergedRecord;
    }

    return overrideValue;
}

function mergeResolvedMerchantConfigStorageValue(
    baseConfig: MerchantConfig,
    storageConfig: MerchantConfigStorage | undefined
): ResolvedMerchantConfigValue {
    return deepMergeResolvedMerchantConfigValue(
        baseConfig,
        MerchantConfigStorageWriteObjectSchema.parse(storageConfig ?? {})
    );
}

function resolveMerchantConfigFromInput(
    config: MerchantConfigInput | undefined
): unknown {
    return deepMergeResolvedMerchantConfigValue(
        createResolvedDefaultMerchantConfig(),
        config ?? {}
    );
}

export function mergeMerchantConfigStorageIntoBaseConfig(
    baseConfig: MerchantConfig,
    storageConfig: MerchantConfigStorage | undefined
): MerchantConfig {
    return MerchantConfigResolvedSchema.parse(
        mergeResolvedMerchantConfigStorageValue(baseConfig, storageConfig)
    );
}

function normalizeResolvedConfigValueForStorage(
    resolvedValue: unknown,
    defaultValue: unknown
): unknown {
    if (resolvedValue === null || resolvedValue === undefined) {
        return undefined;
    }

    if (Array.isArray(resolvedValue)) {
        return resolvedValue.length === 0 &&
            Array.isArray(defaultValue) &&
            defaultValue.length === 0
            ? undefined
            : resolvedValue;
    }

    if (isPlainRecord(resolvedValue)) {
        const defaultRecord = isPlainRecord(defaultValue) ? defaultValue : {};
        const normalizedRecord: Record<string, unknown> = {};
        for (const resolvedKey in resolvedValue) {
            const normalizedEntryValue = normalizeResolvedConfigValueForStorage(
                resolvedValue[resolvedKey],
                defaultRecord[resolvedKey]
            );
            if (normalizedEntryValue !== undefined) {
                normalizedRecord[resolvedKey] = normalizedEntryValue;
            }
        }
        return Object.keys(normalizedRecord).length === 0 ? undefined : normalizedRecord;
    }

    return Object.is(resolvedValue, defaultValue) ? undefined : resolvedValue;
}

const MerchantConfigStorageObjectZodSchema = z.object({
        requires_password_change: z.boolean().optional(),
        businessDetails: BusinessDetailsSchema.nullable().optional(),
        ipg: IpgConfigStorageMapSchema.optional(),
        ipw: createDeepOptionalSchema(
            z.partialRecord(IpwSystemIdSchema, IpwConfigSchema)
        ),
        ips: createDeepOptionalSchema(IpsConfigSchema).optional(),
        binLookupService: BinLookupServiceConfigSchema.nullable().optional(),
        processorConfig: createDeepOptionalSchema(z.array(ProcessorConfigSchema)),
        sambaConfig: StoredSambaConfigSchema.optional(),
        platformConfig: createDeepOptionalSchema(PlatformConfigSchema).optional(),
        userRoleAssignmentConfig:
            UserRoleAssignmentConfigSchema.nullable().optional(),
    });

export const MerchantConfigStorageObjectSchema: z.ZodType<MerchantConfigStorage> =
    MerchantConfigStorageObjectZodSchema;

const MerchantConfigStorageV3ObjectSchema: z.ZodType<
    Omit<MerchantConfigStorage, 'binLookupService'>
> = MerchantConfigStorageObjectZodSchema.omit({ binLookupService: true });

export const LegacyMerchantConfigStorageObjectSchema:
    z.ZodType<LegacyMerchantConfigStorage> = z
    .object({
        requires_password_change: z.boolean().optional(),
        businessDetails: BusinessDetailsSchema.nullable().optional(),
        ipg: createDeepOptionalSchema(
            z.partialRecord(
                IpgSystemIdSchema,
                IpgConfigWithGatewayDefaultsSchema
            )
        ),
        ipw: createDeepOptionalSchema(
            z.partialRecord(IpwSystemIdSchema, IpwConfigSchema)
        ),
        ips: createDeepOptionalSchema(IpsConfigSchema).optional(),
        processorConfig: createDeepOptionalSchema(
            z.array(ProcessorConfigSchema)
        ),
        sambaConfig: StoredSambaConfigSchema.optional(),
        platformConfig: createDeepOptionalSchema(
            PlatformConfigSchema
        ).optional(),
        userRoleAssignmentConfig:
            UserRoleAssignmentConfigSchema.nullable().optional(),
    })
    .superRefine(validateLegacyIpgConfigMap);

export const MerchantConfigStorageV1Schema: z.ZodType<MerchantConfigStorageV1> =
    z.preprocess(
        normalizeLegacyMerchantConfigStorage,
        LegacyMerchantConfigStorageObjectSchema
    );

export const MerchantConfigStorageV2Schema: z.ZodType<MerchantConfigStorageV2> =
    z
        .object({
            merchant_config_version: z.literal(2),
            config: LegacyMerchantConfigStorageObjectSchema,
        })
        .strict();

export const MerchantConfigStorageV3Schema: z.ZodType<MerchantConfigStorageV3> =
    z
        .object({
            merchant_config_version: z.literal(3),
            config: MerchantConfigStorageV3ObjectSchema,
        })
        .strict();

export const MerchantConfigStorageV4Schema: z.ZodType<MerchantConfigStorageV4> =
    z
        .object({
            merchant_config_version: z.literal(4),
            config: MerchantConfigStorageObjectSchema,
        })
        .strict();

export const StoredMerchantConfigStorageV1Schema:
    z.ZodType<MerchantConfigStorageV1> = z.preprocess(
        normalizeStoredMerchantConfigNumericLeaves,
        MerchantConfigStorageV1Schema
    );

export const StoredMerchantConfigStorageV2Schema:
    z.ZodType<MerchantConfigStorageV2> = z.preprocess(
        normalizeStoredMerchantConfigNumericLeaves,
        MerchantConfigStorageV2Schema
    );

export const StoredMerchantConfigStorageV3Schema:
    z.ZodType<MerchantConfigStorageV3> = z.preprocess(
        normalizeStoredMerchantConfigNumericLeaves,
        MerchantConfigStorageV3Schema
    );

export const StoredMerchantConfigStorageV4Schema:
    z.ZodType<MerchantConfigStorageV4> = z.preprocess(
        normalizeStoredMerchantConfigNumericLeaves,
        MerchantConfigStorageV4Schema
    );

function normalizeLegacyMerchantConfigStorageToCurrent(
    config: LegacyMerchantConfigStorage
): MerchantConfigStorage {
    return MerchantConfigStorageObjectSchema.parse({
        ...config,
        ipg: normalizeLegacyIpgConfigMap(config.ipg),
    });
}

export function normalizeMerchantConfigStorageV1(
    config: MerchantConfigStorageV1
): MerchantConfigStorage {
    return normalizeLegacyMerchantConfigStorageToCurrent(config);
}

export function normalizeMerchantConfigStorageV2(
    config: MerchantConfigStorageV2
): MerchantConfigStorage {
    return normalizeLegacyMerchantConfigStorageToCurrent(config.config);
}

export function normalizeMerchantConfigStorageV3(
    config: MerchantConfigStorageV3
): MerchantConfigStorage {
    return MerchantConfigStorageObjectSchema.parse(config.config);
}

export function normalizeMerchantConfigStorageV4(
    config: MerchantConfigStorageV4
): MerchantConfigStorage {
    return config.config;
}

export const MerchantConfigStorageContract = defineStoredJsonContract({
    contractName: 'MerchantConfigStorage',
    semanticFieldValues: {
        businessDetailsDba:
            defineStoredJsonSemanticFieldValue<string>('scalar'),
        businessDetailsLegalEntityName:
            defineStoredJsonSemanticFieldValue<string>('scalar'),
    },
    variants: {
        v1: {
            schemaName: 'StoredMerchantConfigStorageV1Schema',
            schema: StoredMerchantConfigStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['merchant_config_version'],
                },
            ],
            normalize: normalizeMerchantConfigStorageV1,
            semanticPaths: {
                businessDetailsDba: ['businessDetails', 'dba'],
                businessDetailsLegalEntityName: [
                    'businessDetails',
                    'legalEntityName',
                ],
            },
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredMerchantConfigStorageV2Schema',
            schema: StoredMerchantConfigStorageV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['merchant_config_version'],
                    markerValue: MerchantConfigStorageVersion.V2,
                },
            ],
            normalize: normalizeMerchantConfigStorageV2,
            semanticPaths: {
                businessDetailsDba: ['config', 'businessDetails', 'dba'],
                businessDetailsLegalEntityName: [
                    'config',
                    'businessDetails',
                    'legalEntityName',
                ],
            },
            unrepresentedSemanticFields: [],
        },
        v3: {
            schemaName: 'StoredMerchantConfigStorageV3Schema',
            schema: StoredMerchantConfigStorageV3Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['merchant_config_version'],
                    markerValue: MerchantConfigStorageVersion.V3,
                },
            ],
            normalize: normalizeMerchantConfigStorageV3,
            semanticPaths: {
                businessDetailsDba: ['config', 'businessDetails', 'dba'],
                businessDetailsLegalEntityName: [
                    'config',
                    'businessDetails',
                    'legalEntityName',
                ],
            },
            unrepresentedSemanticFields: [],
        },
        v4: {
            schemaName: 'StoredMerchantConfigStorageV4Schema',
            schema: StoredMerchantConfigStorageV4Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['merchant_config_version'],
                    markerValue: MerchantConfigStorageVersion.V4,
                },
            ],
            normalize: normalizeMerchantConfigStorageV4,
            semanticPaths: {
                businessDetailsDba: ['config', 'businessDetails', 'dba'],
                businessDetailsLegalEntityName: [
                    'config',
                    'businessDetails',
                    'legalEntityName',
                ],
            },
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v4',
});

export const StoredMerchantConfigStorageRowParser: z.ZodType<MerchantConfigStorage> =
    buildStoredJsonParser(MerchantConfigStorageContract);

export const StoredMerchantConfigStorageParser: z.ZodType<MerchantConfigStorage> =
    z.preprocess(
        (config: unknown): unknown => ({ value: config }),
        StoredMerchantConfigStorageRowParser
    );

export const MerchantConfigStorageSchema: z.ZodType<MerchantConfigStorage> =
    StoredMerchantConfigStorageParser;

export const MerchantConfigStorageWriteObjectSchema: z.ZodType<MerchantConfigStorage> =
    z.object({
        requires_password_change: z.boolean().optional(),
        businessDetails: BusinessDetailsSchema.nullable().optional(),
        ipg: IpgConfigStorageMapSchema.optional(),
        ipw: createDeepOptionalSchema(
            z.partialRecord(IpwSystemIdSchema, IpwConfigSchema)
        ),
        ips: createDeepOptionalSchema(IpsConfigSchema).optional(),
        binLookupService: BinLookupServiceConfigSchema.nullable().optional(),
        processorConfig: createDeepOptionalSchema(z.array(ProcessorConfigSchema)),
        sambaConfig: StoredSambaConfigWriteSchema.optional(),
        platformConfig: createDeepOptionalSchema(PlatformConfigSchema).optional(),
        userRoleAssignmentConfig:
            UserRoleAssignmentConfigSchema.nullable().optional(),
    });

export const MerchantConfigStorageWriteSchema: z.ZodType<MerchantConfigStorage> =
    z.unknown().transform((config, ctx) =>
        parseUnwrappedCurrentSchema(
            normalizeLegacyMerchantConfigRuntime(config),
            ctx,
            MerchantConfigStorageWriteObjectSchema,
            'merchant_config_version',
            'MerchantConfigStorageV4'
        )
    );

export function serializeMerchantConfigStorageV2(
    config: LegacyMerchantConfigStorage
): MerchantConfigStorageV2 {
    return {
        merchant_config_version: MerchantConfigStorageVersion.V2,
        config: LegacyMerchantConfigStorageObjectSchema.parse(config),
    };
}

function removeUndefinedObjectProperties(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map((entry) => removeUndefinedObjectProperties(entry));
    }
    if (!isPlainRecord(value)) {
        return value;
    }

    const result: Record<string, unknown> = {};
    for (const key in value) {
        const entry = value[key];
        if (entry !== undefined) {
            result[key] = removeUndefinedObjectProperties(entry);
        }
    }
    return result;
}

export function serializeMerchantConfigStorageV4(
    config: MerchantConfigStorage
): MerchantConfigStorageV4 {
    return {
        merchant_config_version: MerchantConfigStorageVersion.V4,
        config: MerchantConfigStorageWriteObjectSchema.parse(
            removeUndefinedObjectProperties(config)
        ),
    };
}

export const MerchantConfigInputObjectSchema: z.ZodType<MerchantConfigInput> =
    z.object({
        requires_password_change: z.boolean().optional(),
        businessDetails: BusinessDetailsSchema.nullable().optional(),
        ipg: IpgConfigStorageMapSchema.optional(),
        ipw: createDeepOptionalSchema(
            z.partialRecord(IpwSystemIdSchema, IpwConfigSchema)
        ),
        rbs: createDeepOptionalSchema(RbsConfigSchema).optional(),
        ips: createDeepOptionalSchema(IpsConfigSchema).optional(),
        binLookupService: BinLookupServiceConfigSchema.nullable().optional(),
        processorConfig: createDeepOptionalSchema(
            z.array(ProcessorConfigSchema)
        ).optional(),
        sambaConfig: createDeepOptionalSchema(SambaConfigSchema).optional(),
        platformConfig:
            createDeepOptionalSchema(PlatformConfigSchema).optional(),
        userRoleAssignmentConfig:
            UserRoleAssignmentConfigSchema.nullable().optional(),
    });

export const MerchantConfigResolvedSchema: z.ZodType<MerchantConfig> =
    MerchantConfigObjectSchema.superRefine(
        validateMerchantConfigNestedAllowedWebsiteSystemIds
    );

export const MerchantConfigSchema: z.ZodType<MerchantConfig> =
    z.unknown().transform((config, ctx) =>
        parseUnwrappedCurrentSchema(
            normalizeLegacyMerchantConfigRuntime(config),
            ctx,
            MerchantConfigInputObjectSchema,
            'merchant_config_version',
            'MerchantConfigStorageV4'
        )
    ).transform((config) =>
        resolveMerchantConfigFromInput(config)
    ).pipe(MerchantConfigResolvedSchema);

export function normalizeMerchantConfigStorage(
    config: MerchantConfig
): MerchantConfigStorage {
    const normalizedConfigValue = normalizeResolvedConfigValueForStorage(
        config,
        createResolvedDefaultMerchantConfig()
    );

    return MerchantConfigStorageWriteObjectSchema.parse(
        isPlainRecord(normalizedConfigValue) ? normalizedConfigValue : {}
    );
}

export const resolveMerchantConfigIpsConfig = (
    merchantConfig?: MerchantConfig
): IpsConfig => MerchantConfigSchema.parse(merchantConfig ?? {}).ips;

export const resolveMerchantConfigAddressRequiredForCardPayment = (
    merchantConfig: MerchantConfig | undefined
): boolean | undefined =>
    resolveMerchantConfigIpsConfig(merchantConfig).addressRequiredForCardPayment;

export function resolveMerchantConfigEnablePaymentsInPillar(
    merchantConfig: MerchantConfig | undefined
): boolean {
    return (
        resolveMerchantConfigIpsConfig(merchantConfig)
            .enablePaymentsInPillar === true
    );
}

export const RoleAssignmentIpsConfigSchema: z.ZodType<DeepPartial<IpsConfig>> =
    createDeepOptionalSchema(IpsConfigSchema);

export type RoleAssignmentCustomConfig = DeepPartial<MerchantConfig>;

export type LegacyMerchantConfig = Omit<MerchantConfig, 'ipg' | 'binLookupService'> & {
    ipg: Partial<Record<IpgSystemIdKey, LegacyIpgConfig>>;
};

export type LegacyRoleAssignmentCustomConfig =
    DeepPartial<LegacyMerchantConfig>;

export const RoleAssignmentCustomConfigStorageVersion = {
    V1: 1,
    V2: 2,
    V3: 3,
} as const;
export type RoleAssignmentCustomConfigStorageVersion =
    (typeof RoleAssignmentCustomConfigStorageVersion)[keyof typeof RoleAssignmentCustomConfigStorageVersion];

export type RoleAssignmentCustomConfigV1 =
    LegacyRoleAssignmentCustomConfig;

export interface RoleAssignmentCustomConfigV2 {
    readonly role_assignment_custom_config_version:
        typeof RoleAssignmentCustomConfigStorageVersion.V2;
    readonly config: LegacyRoleAssignmentCustomConfig;
}

export interface RoleAssignmentCustomConfigV3 {
    readonly role_assignment_custom_config_version:
        typeof RoleAssignmentCustomConfigStorageVersion.V3;
    readonly config: RoleAssignmentCustomConfig;
}

export type StoredRoleAssignmentCustomConfig =
    | RoleAssignmentCustomConfigV1
    | RoleAssignmentCustomConfigV2
    | RoleAssignmentCustomConfigV3;

export const RoleAssignmentCustomConfigObjectSchema: z.ZodType<RoleAssignmentCustomConfig> =
    MerchantConfigInputObjectSchema;

export const LegacyMerchantConfigObjectSchema:
    z.ZodType<LegacyMerchantConfig> = z.object({
    requires_password_change: z.boolean().default(false),
    businessDetails: BusinessDetailsSchema.nullable(),
    ipg: z.partialRecord(
        IpgSystemIdSchema,
        IpgConfigWithGatewayDefaultsSchema
    ),
    ipw: z.partialRecord(IpwSystemIdSchema, IpwConfigSchema),
    rbs: RbsConfigSchema.default({ websites: [] }),
    ips: IpsConfigSchema,
    processorConfig: z.array(ProcessorConfigSchema),
    sambaConfig: SambaConfigSchema,
    platformConfig: PlatformConfigSchema,
    userRoleAssignmentConfig: UserRoleAssignmentConfigSchema.nullable(),
});

export const LegacyRoleAssignmentCustomConfigObjectSchema:
    z.ZodType<LegacyRoleAssignmentCustomConfig> =
    createDeepOptionalSchema(
        LegacyMerchantConfigObjectSchema
    ).superRefine(validateLegacyIpgConfigMap);

export const RoleAssignmentCustomConfigSchema: z.ZodType<RoleAssignmentCustomConfig> =
    z.unknown().transform((config, ctx) =>
        parseUnwrappedCurrentSchema(
            config,
            ctx,
            RoleAssignmentCustomConfigObjectSchema,
            'role_assignment_custom_config_version',
            'RoleAssignmentCustomConfigV3'
        )
    );

export const RoleAssignmentCustomConfigVersionSchema: z.ZodType<RoleAssignmentCustomConfigStorageVersion> =
    z.union([z.literal(1), z.literal(2), z.literal(3)]);

export const RoleAssignmentCustomConfigV1Schema: z.ZodType<RoleAssignmentCustomConfigV1> =
    LegacyRoleAssignmentCustomConfigObjectSchema;

export const RoleAssignmentCustomConfigV2Schema: z.ZodType<RoleAssignmentCustomConfigV2> =
    z
        .object({
            role_assignment_custom_config_version: z.literal(2),
            config: LegacyRoleAssignmentCustomConfigObjectSchema,
        })
        .strict();

export const RoleAssignmentCustomConfigV3Schema: z.ZodType<RoleAssignmentCustomConfigV3> =
    z
        .object({
            role_assignment_custom_config_version: z.literal(3),
            config: RoleAssignmentCustomConfigObjectSchema,
        })
        .strict();

export const StoredRoleAssignmentCustomConfigV1Schema:
    z.ZodType<RoleAssignmentCustomConfigV1> = z.preprocess(
        normalizeStoredMerchantConfigNumericLeaves,
        RoleAssignmentCustomConfigV1Schema
    );

export const StoredRoleAssignmentCustomConfigV2Schema:
    z.ZodType<RoleAssignmentCustomConfigV2> = z.preprocess(
        normalizeStoredMerchantConfigNumericLeaves,
        RoleAssignmentCustomConfigV2Schema
    );

export const StoredRoleAssignmentCustomConfigV3Schema:
    z.ZodType<RoleAssignmentCustomConfigV3> = z.preprocess(
        normalizeStoredMerchantConfigNumericLeaves,
        RoleAssignmentCustomConfigV3Schema
    );

export const RoleAssignmentCustomConfigVersionedObjectMarkerSchema: z.ZodType<{
    readonly role_assignment_custom_config_version: unknown;
}> = z
    .object({
        role_assignment_custom_config_version: z.unknown(),
    })
    .passthrough();

export const StoredRoleAssignmentCustomConfigSchema: z.ZodType<StoredRoleAssignmentCustomConfig> =
    z.union([
        StoredRoleAssignmentCustomConfigV3Schema,
        StoredRoleAssignmentCustomConfigV2Schema,
        StoredRoleAssignmentCustomConfigV1Schema,
    ]);

export function normalizeRoleAssignmentCustomConfigV1(
    config: RoleAssignmentCustomConfigV1
): RoleAssignmentCustomConfig {
    return RoleAssignmentCustomConfigObjectSchema.parse({
        ...config,
        ipg: normalizeLegacyIpgConfigMap(config.ipg),
    });
}

export function normalizeRoleAssignmentCustomConfigV2(
    config: RoleAssignmentCustomConfigV2
): RoleAssignmentCustomConfig {
    return normalizeRoleAssignmentCustomConfigV1(config.config);
}

export function normalizeRoleAssignmentCustomConfigV3(
    config: RoleAssignmentCustomConfigV3
): RoleAssignmentCustomConfig {
    return config.config;
}

export const RoleAssignmentCustomConfigStorageContract = defineStoredJsonContract({
    contractName: 'RoleAssignmentCustomConfig',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredRoleAssignmentCustomConfigV1Schema',
            schema: StoredRoleAssignmentCustomConfigV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['role_assignment_custom_config_version'],
                },
            ],
            normalize: normalizeRoleAssignmentCustomConfigV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredRoleAssignmentCustomConfigV2Schema',
            schema: StoredRoleAssignmentCustomConfigV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['role_assignment_custom_config_version'],
                    markerValue: RoleAssignmentCustomConfigStorageVersion.V2,
                },
            ],
            normalize: normalizeRoleAssignmentCustomConfigV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v3: {
            schemaName: 'StoredRoleAssignmentCustomConfigV3Schema',
            schema: StoredRoleAssignmentCustomConfigV3Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['role_assignment_custom_config_version'],
                    markerValue: RoleAssignmentCustomConfigStorageVersion.V3,
                },
            ],
            normalize: normalizeRoleAssignmentCustomConfigV3,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v3',
});

export const StoredRoleAssignmentCustomConfigParser:
    z.ZodType<RoleAssignmentCustomConfig> =
    buildStoredJsonValueParser(RoleAssignmentCustomConfigStorageContract);

export function serializeRoleAssignmentCustomConfigV2(
    config: LegacyRoleAssignmentCustomConfig
): RoleAssignmentCustomConfigV2 {
    return {
        role_assignment_custom_config_version:
            RoleAssignmentCustomConfigStorageVersion.V2,
        config: LegacyRoleAssignmentCustomConfigObjectSchema.parse(config),
    };
}

export function serializeRoleAssignmentCustomConfigV3(
    config: RoleAssignmentCustomConfig
): RoleAssignmentCustomConfigV3 {
    return {
        role_assignment_custom_config_version:
            RoleAssignmentCustomConfigStorageVersion.V3,
        config: RoleAssignmentCustomConfigObjectSchema.parse(
            removeUndefinedObjectProperties(config)
        ),
    };
}

function allowedWebsiteKey(allowedWebsite: AllowedWebsite): string {
    return [
        allowedWebsite.type,
        String(allowedWebsite.system_id),
        allowedWebsite.template_name ?? '',
        allowedWebsite.name,
        allowedWebsite.url,
        allowedWebsite.sessionPersistence,
        allowedWebsite.promoteSessionCookies ? 'promote' : 'do-not-promote',
        allowedWebsite.allowedUrlPatterns.join('\u0000'),
        allowedWebsite.fetchDefaultAction,
        stringifyLosslessJson(allowedWebsite.urlPatternsRules),
        allowedWebsite.iframeDefaultAction,
        stringifyLosslessJson(allowedWebsite.iframeUrlPatterns),
    ].join('\u0001');
}

export function allowedWebsitesOfMerchantConfig(merchantConfig: MerchantConfig): AllowedWebsite[] {
    const ipwAllowedWebsites = Object.values(merchantConfig.ipw).flatMap(
        (ipw): AllowedWebsite[] => {
            switch (ipw.ipw_system_id) {
                case IpwSystemIdKey.DEALERTRACK:
                case IpwSystemIdKey.PCPARTPICKER:
                    return ipw.allowedWebsites;
                default: {
                    const exhaustiveIpwConfig: never = ipw;
                    throw new Error(
                        `Unsupported IPW config: ${stringifyLosslessJson(exhaustiveIpwConfig)}`
                    );
                }
            }
        }
    );
    const combinedAllowedWebsites: AllowedWebsite[] = [
        ...Object.values(merchantConfig.ipg).flatMap((ipg) => ipg.allowedWebsites),
        ...ipwAllowedWebsites,
        ...(merchantConfig.sambaConfig?.unmanagedAllowedWebsites ?? []),
        ...merchantConfig.processorConfig.flatMap(
            (processorConfig): AllowedWebsite[] =>
                processorConfig.reporting_dashboard === undefined
                    ? []
                    : [processorConfig.reporting_dashboard]
        ),
    ];
    const dedupedAllowedWebsites = new Map<string, AllowedWebsite>();

    for (const allowedWebsite of combinedAllowedWebsites) {
        dedupedAllowedWebsites.set(allowedWebsiteKey(allowedWebsite), allowedWebsite);
    }

    return Array.from(dedupedAllowedWebsites.values());
}

export function rpaModulesOfMerchantConfig(merchantConfig: MerchantConfig): RpaModuleName[] {
    return [
        ...Object.values(merchantConfig.ipg).flatMap((ipg) => ipg.rpaModules),
        ...Object.values(merchantConfig.ipw).flatMap((ipw) => ipw.rpaModules),
    ]
}
